import { type Decor, placeDecor } from './decor.ts';
import { BTN_DOWN, BTN_FIRE, type Body, HIP_X, HIP_Y, copyBody, newBody, shoulderAt, stepBody } from '../shared/actor.ts';
import { WoundBook, beastDamage, beastScars } from './wounds.ts';
import type { Reader } from '../shared/codec.ts';
import { ACTOR_H, ACTOR_MAX_HP, ACTOR_W, CHUNK, CHUNK_COUNT, CHUNK_SHIFT, CHUNKS_X, DT, GRAVITY, TICK_RATE, WORLD_H, WORLD_W } from '../shared/constants.ts';
import { type BeastState, type CraftState, type FrameHandler, type KillInfo, type RemoteActor, type RoundState, type SelfCraftState, type SelfState, type SelfTankState, type ShipState, type TankState, type MineState, type GeyserState, type BurnerState, type FootState, type ChargeState, applyFrameRecords } from '../shared/frame.ts';
import { STICKY_FUSE, stickyBeepAt } from '../shared/sticky.ts';
import { BURNER_FUSE, BurnerPhase, CLOUD_MS, FOOT_LIFE, beepAt } from '../shared/stoneburner.ts';
import { BeastKind, CORAL_LIMBS, POSSESS_RISE } from '../shared/wildlife.ts';
import { ENGINE_NOZZLE_Y, ENGINE_X, SHIP_H, SHIP_RIDERS, SHIP_W, shipPoint, shipSeat } from '../shared/dropship.ts';
import { TANK_W, type Tank, isDog, isPet, isSpider, newTank, surfSeat, stepTank, tankH, tankW } from '../shared/tank.ts';
import { FACTIONS } from '../shared/factions.ts';
import { Collider, DistanceField } from '../shared/field.ts';
import { Projectiles, pickHeat } from '../shared/kernels.ts';
import { ActorField, MAX_ACTORS, PK, Particles, W_BURN, W_CRAFT, W_DEBRIS, W_SHIP, W_TANK, W_TRAP, W_LASER, releaseCarve, spillGold, W_RAM, W_ROCKFALL, W_GEYSER, W_BLISTER, W_VENOM, W_ASP, W_COIL, W_SEED, W_BASILISK, W_STONEBURNER, W_DRAGON, W_SPIDER, NO_OWNER } from '../shared/particles.ts';
import { type Craft, CraftPart, craftHalfExtents, newCraft, newCraftStep, stepCraft } from '../shared/craft.ts';
import { CALL_COST, CallKind, F_ALIVE, F_FIRING, F_GROUND, F_JET, GF_DEAD, GF_RUMBLE, GF_TOXIC, GameMode, MOLE_COST, Phase, TARANTULA_COST, Team, WATCHDOG_COST, classOfFlags, dequantizeAim } from '../shared/protocol.ts';
import { Rng } from '../shared/rng.ts';
import { type Flora, FloraKind, floraBox, GEYSER_CLOUD_R, GEYSER_TOXIC_TICKS, geyserBurst, geyserCloud } from '../shared/frosting.ts';
import { MAT_COLOR, Mat } from '../shared/materials.ts';
import { Terrain } from '../shared/terrain.ts';
import { generateWorld, lastBiome, lastCaves, lastComplexes, lastDungeon, lastSiege , lastFlora, lastValley } from '../shared/worldgen.ts';
import { type Valley } from '../shared/valley.ts';
import { WATER_TICKS, paintWater } from '../shared/water.ts';
import type { SiegeMap } from '../shared/siege.ts';
import { type Relic, placeRelics } from './relics.ts';
import type { Dungeon } from '../shared/dungeon.ts';
import { LASER_MAX, BLAST_IMPULSE, PROJ, PROJ_BUILD, ProjKind, SHOULDER_X, SHOULDER_Y, WEAPONS, WeaponId, projName, weaponOfProj } from '../shared/weapons.ts';
import { type BuildBlocker, canBuild, pieceOf } from '../shared/build.ts';
import { type GroundItem, NO_WEAPON, PICKUP_R, invByte, stepItem } from '../shared/items.ts';
import { bileVomit, coreFlare, dragonBreathGout, crabBloodGibs, crabBloodSplat, stonePlume, fireSpitBurst, fireSpitTrail, moltenCore, stoneburnBlast, droidHit, droidPartOff, droidSocket, droidWreck, smokeTrail, plasmaTrail, bloodSplat, gooSplat, aspGibs, ichorSplat, spiderGibs, ASP_GOO, bulletImpact, craftDebris, craftExhaust, craftPartOff, engineExhaust, heavyMuzzle, materialize, digDust, explosion, gibBurst, jetExhaust, droidJets, limbOff, muzzle, laserHit, rocketTrail, spiderJets, vaporPuff, shipDownwash, slugImpact, slugTrail, stumpDrip, tankDebris, tankJets, tankPartOff, blisterBurst, floraGibs, beastGibs, coralBurst, coralSpores, venomDrip, venomSplash, venomTrail } from './effects.ts';
import { floraColors } from './flora-palette.ts';
import { ALL_PARTS, ClassId, type Mobility, PART_COUNT, Part, has, headless, mobility } from '../shared/body.ts';
import { cleanChat, inert } from '../shared/text.ts';
import type { Coach } from './tutorial.ts';

const TICK_MS = 1000 / TICK_RATE;
/** Remote actors are rendered this many ticks in the past for smooth interpolation. */
const INTERP_TICKS = 3;

export interface PlayerInfo {
  id: number;
  name: string;
  kills: number;
  deaths: number;
  gold: number;
  wins: number;
  /** Creatures killed. */
  beasts: number;
  /** Clones of their own side killed (each a point off). */
  teamKills: number;
  bot: boolean;
  color: string;
  rgb: number;
}

interface Snap {
  tick: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  aim: number;
  flags: number;
  hp: number;
  weapon: number;
  parts: number;
  stance: number;
  faction: number;
}

export interface RemoteView {
  id: number;
  x: number;
  y: number;
  aim: number;
  flags: number;
  hp: number;
  weapon: number;
  moving: boolean;
  parts: number; // attached-part mask (body.ts)
  stance: number; // actor.ts Stance
  faction: number; // factions.ts: this clone's vendor
  vx: number;
  vy: number;
}

export interface CraftView extends CraftState {}
export interface TankView extends TankState {}
export interface ShipView extends ShipState {}
/** A line of the radio's menu: a call (and its price), or the remote (free). */
export interface RadioEntry {
  kind: number;
  name: string;
  blurb: string;
  cost: number;
  free?: boolean;
}

/** Dropship scrap: grey-blue gunmetal. */
const SHIP_SCRAP = 0x6e7a86;

/** Where a driver's clone sits inside its tank (top-left of the clone, from the tank's). */
const SEAT_X = TANK_W / 2 - ACTOR_W / 2;
const SEAT_Y = 2;
/** In ridingNow's codes: the vehicle is a dropship, not a tank. */
const SHIP_RIDE = 1 << 16;

const wrapAngle = (a: number) => a - Math.PI * 2 * Math.floor((a + Math.PI) / (Math.PI * 2));

export interface FeedItem {
  text: string;
  color: string;
  at: number;
  /** A kill-feed line (part of the event stream, hidden with the info panel), not a notice for us. */
  kill?: boolean;
}

/** A drop rocket with nobody aboard: bare gunmetal (as drawn). */
const CRAFT_GUNMETAL = 0x8a9096;

/** How long a muzzle flash shows after a shot (ms): two frames or so. */
export const SHOT_FLASH_MS = 70;

export interface Flash {
  x: number;
  y: number;
  r: number;
  at: number;
}

function matRgb(m: number): number {
  const [r, g, b] = MAT_COLOR[m] ?? MAT_COLOR[1];
  return (r << 16) | (g << 8) | b;
}

function seqNewer(a: number, b: number): boolean {
  const d = (a - b) & 0xffff;
  return d !== 0 && d < 0x8000;
}

function playerColor(id: number): { css: string; rgb: number } {
  const hue = (id * 137.508) % 360;
  const s = 0.65;
  const l = 0.55;
  const k = (n: number) => (n + hue / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const r = Math.round(f(0) * 255);
  const g = Math.round(f(8) * 255);
  const b = Math.round(f(4) * 255);
  return { css: `rgb(${r},${g},${b})`, rgb: (r << 16) | (g << 8) | b };
}

/** Team colours: red and green fatigues, and Extraction's blue and gold (Team.Red .. Team.Gold). */
export const TEAM_COLORS = [
  { css: 'rgb(222,64,56)', rgb: 0xde4038 },
  { css: 'rgb(76,190,72)', rgb: 0x4cbe48 },
  { css: 'rgb(70,130,236)', rgb: 0x4682ec },
  { css: 'rgb(236,190,52)', rgb: 0xecbe34 },
] as const;

/** What the game tells the sound effects (sfx.ts; structural, so the game needs no DOM audio types). */
export interface Sfx {
  listen(x: number, y: number): void;
  shot(kind: number, x: number, y: number, owner: number): void;
  explode(x: number, y: number, radius: number): void;
  impact(x: number, y: number): void;
  hit(x: number, y: number, amount: number, synthetic: boolean): void;
  limb(x: number, y: number, synthetic: boolean): void;
  gib(x: number, y: number, violence: number, synthetic: boolean): void;
  laser(x0: number, y0: number, x1: number, y1: number, power: number): void;
  engines(level: number, pan: number): void;
  jets(level: number, pan: number): void;
  charge(level: number): void;
  /** The Valley of Souls' calm, 0..1. */
  calm(level: number): void;
  /** A creature rearing up, hissing (a millipede's deeper, an asp's sharp). */
  hiss(x: number, y: number, big: boolean): void;
  buzz(x: number, y: number): void;
  roar(x: number, y: number): void;
  rattle(x: number, y: number): void;
  /** The Stoneburner: its beacon's beep (`urgency` 0..1 as the countdown runs out), the missile's scream, the strike. */
  beep(x: number, y: number, urgency: number): void;
  incoming(x: number, y: number): void;
  stoneburn(x: number, y: number): void;
}

const CAREER_KEY = 'sc.career';
/** The browser's storage, if any (typed by hand: the game also builds without the DOM types). */
const store = () => (globalThis as { localStorage?: { getItem(k: string): string | null; setItem(k: string, v: string): void } }).localStorage;

/** Lifetime kills and deaths from this browser's storage (zeros where there is none). */
function loadCareer(): { kills: number; deaths: number } {
  try {
    const v = JSON.parse(store()?.getItem(CAREER_KEY) ?? 'null');
    if (v && Number.isFinite(v.kills) && Number.isFinite(v.deaths)) return { kills: v.kills, deaths: v.deaths };
  } catch {
    // (no storage here, or junk in it)
  }
  return { kills: 0, deaths: 0 };
}

function saveCareer(c: { kills: number; deaths: number }): void {
  try {
    store()?.setItem(CAREER_KEY, JSON.stringify(c));
  } catch {
    // (storage blocked: the record lasts the session)
  }
}

/** Kills per death (kills alone while deathless). */
/** A clone's score: clones killed, plus creatures killed, less a point for each of its own side it killed. */
export const scoreOf = (p: { kills: number; beasts: number; teamKills: number }) => p.kills + p.beasts - p.teamKills;
export const kdRatio = (kills: number, deaths: number) => kills / Math.max(1, deaths);

/** A creature in view, as the client keeps it (Game.beastViews). */
/** Ticks a dead creature's segment lies at rest before it's worked into the ground (the remains). */
const SEGMENT_BURY = 45;

export interface BeastView {
  kind: number;
  seed: number;
  hp: number;
  /** The most of its health this client has seen (its wounds are measured from it: wounds.ts). */
  top: number;
  lift: number;
  prevLift: number;
  attached: boolean;
  frozen: boolean;
  coiled: boolean;
  gem: boolean;
  frenzy: boolean;
  /** A cave spider carrying a torn-off limb home. */
  carry: boolean;
  /** Infected by the coral: the hive's (crusted, spined, glowing; twice as strong). */
  infected: boolean;
  /** Its body points as last sent, and the frame before. */
  prev: number[];
  cur: number[];
  /** Its recent states by server tick (newest last), for Game.beastPose. */
  snaps: { tick: number; pts: number[]; lift: number }[];
}

/** Note a creature's state as of server tick `tick` (its latest points and rearing). */
function pushBeastSnap(v: BeastView, tick: number): void {
  const s = v.snaps;
  if (s.length && s[s.length - 1].tick >= tick) s.pop(); // (a resent tick: the newer word)
  s.push({ tick, pts: v.cur, lift: v.lift });
  if (s.length > 8) s.shift();
}

export class Game implements FrameHandler {
  readonly terrain = new Terrain();
  /** Every clone's wounds, where they were hit (wounds.ts). */
  readonly wounds = new WoundBook();
  /** Which way we face (the renderer keeps it: our wounds are pinned as we face). */
  myLeft = false;
  readonly loaded = new Uint8Array(CHUNK_COUNT);
  /**
   * Highest solid cell ever seen per column: everything below it is
   * "underground" and gets the cave backdrop once dug out.
   */
  readonly skyline = new Int16Array(WORLD_W).fill(WORLD_H);
  readonly projectiles = new Projectiles(2048);
  /** Scratch for the heat seekers' target list (as this client sees the vehicles). */
  private readonly heat: { x: number; y: number }[] = [];
  /** (Wire the seekers to this client's eyes.) */
  private readonly seekerWired = (this.projectiles.seeker = (i, out) => this.heatTarget(i, out));

  /** A heat seeker's target, from this client's view of the vehicles (the server's word is final where it lands). */
  private heatTarget(i: number, out: { x: number; y: number }): boolean {
    const pr = this.projectiles;
    const owner = pr.owner[i];
    const team = owner < 64 ? (this.teamOf[owner] ?? Team.None) : Team.None;
    const foe = (id: number, t: number) => id !== owner && !(team !== Team.None && t === team);
    const list = this.heat;
    list.length = 0;
    for (const t of this.tankViews()) {
      const who = t.pilot !== 255 ? t.pilot : t.owner;
      if (who !== 255 && foe(who, this.teamOf[who] ?? Team.None)) list.push({ x: t.x + tankW(t) / 2, y: t.y + tankH(t) / 2 });
    }
    for (const sh of this.shipViews()) if (!sh.leaving && foe(sh.owner, sh.team)) list.push({ x: sh.x + SHIP_W / 2, y: sh.y + SHIP_H / 2 });
    for (const c of this.craftViews()) if (c.passenger !== 255 && foe(c.passenger, this.teamOf[c.passenger] ?? Team.None)) list.push({ x: c.x, y: c.y });
    return pickHeat(pr.x[i], pr.y[i], pr.ang[i] || Math.atan2(pr.vy[i], pr.vx[i]), list, out);
  }
  /**
   * One field-engine instance for everything that flies on this client:
   * mirrored terrain grains plus every spark, flame, smoke puff, blood drop
   * and gib. Same pass, same fields, same pixel buffer.
   */
  readonly particles = new Particles(40000);
  private readonly bodyField = new ActorField(ACTOR_W, ACTOR_H);
  /** Per-cell blood stain intensity (0-255). Cosmetic, never networked. */
  readonly stain = new Uint8Array(WORLD_W * WORLD_H);
  /** Whose blood each stain is: 0 red, 1..3 an asp's goo (ASP_GOO, by gem). */
  readonly stainTint = new Uint8Array(WORLD_W * WORLD_H);
  private readonly particleHooks = {
    stain: (x: number, y: number, color: number) => {
      if (x < 0 || y < 0 || x >= WORLD_W || y >= WORLD_H) return;
      const c = y * WORLD_W + x;
      this.stain[c] = Math.min(255, this.stain[c] + 110);
      const goo = ASP_GOO.findIndex((g) => g === color);
      this.stainTint[c] = color ? (goo >= 0 ? goo + 1 : this.stainTint[c]) : 0;
      this.terrain.markRenderDirty(x, y);
    },
    bury: (x: number, y: number, spin: number, aux: number, color: number) => {
      this.buried.push({ x, y, spin, aux, color });
    },
  };
  /**
   * Remains: what gibs left as they settled, worked into the ground. A
   * settled gib isn't faded out: its pixels are stamped into this layer
   * (cell index -> its colour, 0xAABBGGRR, dried a shade darker), and the
   * terrain draws them in the open cells they lie in, as part of the
   * ground. Cosmetic, never networked; blasted or dug away with the ground
   * round them. (The renderer does the stamping, from `buried` and
   * `buriedSegments`: it has the sprites.)
   */
  readonly remains = new Map<number, number>();
  /** Gibs (particles) settled this tick, to be stamped into the remains by the renderer. */
  readonly buried: { x: number; y: number; spin: number; aux: number; color: number }[] = [];
  /** A dead creature's segments settled this tick, likewise. */
  readonly buriedSegments: { x: number; y: number; a: number; kind: number; seed: number; i: number }[] = [];
  readonly field = new DistanceField(this.terrain);
  readonly collider = new Collider(this.terrain, this.field);
  readonly players = new Map<number, PlayerInfo>();
  readonly feed: FeedItem[] = [];
  readonly chatLog: FeedItem[] = [];
  /** The tutorial's prompt for this moment (main.ts sets it), or null outside the tutorial. */
  coach: Coach | null = null;
  readonly flashes: Flash[] = [];
  radar: { id: number; x: number; y: number }[] = [];

  myId = -1;
  room = '';

  // Own clone: predicted body plus the previous tick's body for render interpolation.
  readonly body: Body = newBody(0, 0);
  readonly prevBody: Body = newBody(0, 0);
  alive = false;
  hp = 0;
  /** Own body: attached-part mask and 0..100 health per part (HUD paper doll). */
  parts = ALL_PARTS;
  partHp: number[] = new Array(PART_COUNT).fill(100);
  private readonly mob: Mobility = { legs: 2, jet: true, canFire: true, oneHanded: false };
  respawnTicks = 0;
  /**
   * What we carry (server truth: weapon and rounds in its magazine) and the
   * slot in hand. The selection is ours, applied at once and sent each tick;
   * when the server changes the inventory (pick-up, drop, death) its version
   * moves on and we take its slot.
   */
  inv: { weapon: number; ammo: number }[] = [];
  slot = 0;
  invVersion = -1;
  private pickupHold = 0;
  private dropHold = 0;
  /** Last Man Standing round state (null in sandbox rooms), and who we watch while out. */
  roundState: RoundState | null = null;
  spectate = 255;
  /** Chunks whose regenerated contents didn't match the server's hash: ask for them. */
  readonly resyncWanted: number[] = [];
  /** Weapons lying on the ground near us, simulated like the server does. */
  readonly groundItems = new Map<number, GroundItem>();
  cooldown = 0;
  /** Ticks left reloading the weapon in hand (server truth). */
  reloadLeft = 0;
  /** Own gold, exact (from our own record, not the once-a-second scoreboard). */
  gold = 0;
  /** The current map's biome (worldgen Biome). */
  biome = 0;
  /** The current map's kind (worldgen.ts MapKind): DUNE's banner differs. */
  mapKind = 0;
  /** Is the current map a cave map (a tunnel highway and citadels under it)? */
  caves = false;
  /** Traces of whoever was here first (relics.ts): rare, cosmetic. */
  relics: Relic[] = [];
  /** Siege: the map's layout (the fortress's end, the landing zone), or null. */
  siege: SiegeMap | null = null;
  /** The bunkers' fittings this wave (purely for looks). */
  decor: Decor[] = [];
  /** Where the alien flora grows on this map (frosting.ts; drawn by flora-art.ts while its ground stands). */
  flora: Flora[] = [];
  /** Which of them are gone, and the blister corals swelling to burst (R_FLORA). */
  floraDead: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  /** The Valley of Souls, on its maps (its dome, rooms and calm), else null. */
  valley: Valley | null = null;
  floraSwell = new Set<number>();
  /** The bunkers' steel doors this wave (their cells, and whose). */
  doors: { x0: number; y0: number; x1: number; y1: number; team: number }[] = [];

  /** Scope lock-on (scope.ts): the clone locked onto and the point on it (hitbox-local), and the aim it gives. */
  scopeLock: { id: number; lx: number; ly: number } | null = null;
  /** What the aim assist (or the scope's lock) has snapped onto: the little target marker's world point. */
  aimMark: { x: number; y: number } | null = null;
  /** Can the shot in hand reach the mark? (A lobbed one falls short of anything too far or too high.) */
  aimReach = true;
  lockAim: number | null = null;

  /** Extraction: this map's labyrinth (built from the seed, like the server's), and which traps have gone off. */
  dungeon: Dungeon | null = null;
  trapSpent: Uint8Array = new Uint8Array(32);
  traps(spent: Uint8Array): void {
    this.trapSpent = spent;
  }
  trapGone(id: number): boolean {
    return (this.trapSpent[id >> 3] & (1 << (id & 7))) !== 0;
  }

  /** Sound effects (main.ts plugs them in once audio is allowed; none in tests). */
  sfx: Sfx | null = null;

  /** Where we hear (and gib) from: the clone being watched while spectating, else our own. */
  private ear(): { x: number; y: number } {
    const watched = !this.alive && this.spectate !== 255 ? this.snaps.get(this.spectate)?.at(-1) : undefined;
    return watched ?? this.lastSelf ?? this.body;
  }

  /** Beams to fade out: materializer (builder muzzle to piece centre) and sniper tracers (muzzle to impact). */
  readonly beams: { x0: number; y0: number; x1: number; y1: number; at: number; tracer?: boolean }[] = [];
  /** Laser beams to draw, fading (power 0..1), and the last few beam ids seen. */
  readonly laserBeams: { x0: number; y0: number; x1: number; y1: number; power: number; at: number }[] = [];
  private beamSeen: number[] = [];
  /** Our own laser's charge (ticks held), tracked locally for the charge meter and muzzle glow. */
  laserCharge = 0;

  beam(seq: number, x0: number, y0: number, x1: number, y1: number, power: number, owner: number, vapor = false): void {
    if (this.beamSeen.includes(seq)) return;
    this.beamSeen.push(seq);
    if (this.beamSeen.length > 16) this.beamSeen.shift();
    // A clone's laser leaves its barrel as drawn here (not where the server had it a moment ago).
    if (owner < 64 && !this.tankPilots.has(owner)) {
      const m = this.drawnMuzzle(owner, WEAPONS[WeaponId.Laser].muzzle, Math.atan2(y1 - y0, x1 - x0), x0, y0);
      if (m) {
        x0 = m.x;
        y0 = m.y;
      }
    }
    this.laserBeams.push({ x0, y0, x1, y1, power, at: performance.now() });
    if (this.laserBeams.length > 12) this.laserBeams.shift();
    laserHit(this.particles, x1, y1, (x1 - x0) / (Math.hypot(x1 - x0, y1 - y0) || 1), (y1 - y0) / (Math.hypot(x1 - x0, y1 - y0) || 1), power);
    if (vapor) vaporPuff(this.particles, x1, y1, (x1 - x0) / (Math.hypot(x1 - x0, y1 - y0) || 1), (y1 - y0) / (Math.hypot(x1 - x0, y1 - y0) || 1));
    this.sfx?.laser(x0, y0, x1, y1, power);
    if (owner < 64) this.kicks.set(owner, { at: performance.now(), k: 0.3 + power * 0.7 });
    const me = this.body;
    const d = Math.min(Math.hypot(me.x - x0, me.y - y0), Math.hypot(me.x - x1, me.y - y1));
    this.shake = Math.max(this.shake, (owner === this.myId ? 1 + power * 9 : 0) + Math.max(0, 1 - d / 300) * power * 6);
  }

  /** Health waves spreading (a repair kit used): where, whose side, who used it, when. */
  readonly healWaves: { x: number; y: number; team: number; owner: number; at: number }[] = [];
  private healSeen: number[] = [];

  heal(seq: number, x: number, y: number, team: number, owner: number): void {
    if (this.healSeen.includes(seq)) return;
    this.healSeen.push(seq);
    if (this.healSeen.length > 16) this.healSeen.shift();
    this.healWaves.push({ x, y, team, owner, at: performance.now() });
    if (this.healWaves.length > 8) this.healWaves.shift();
  }

  /** A vehicle's jets: a tank's two, or a tarantula's belly rockets as it glides. */
  private vehicleJets(t: { x: number; y: number; vx: number; vy: number; kind: number; s: number }): void {
    if (isSpider(t)) spiderJets(this.particles, t.x, t.y, tankW(t), tankH(t), t.vx, t.vy);
    else tankJets(this.particles, t.x, t.y, t.vx, t.vy);
  }

  /** Recent shots by clone id (when, how hard), for the gun kicking back in their hands. */
  readonly kicks = new Map<number, { at: number; k: number }>();
  /** How far a clone's gun is kicked back right now (0..1). */
  /**
   * Did clone `id`'s gun just go off (a round or a beam out of it, not just
   * the trigger down)? The muzzle flash shows only then.
   */
  shotFlash(id: number, now: number): boolean {
    const r = this.kicks.get(id);
    return !!r && now - r.at < SHOT_FLASH_MS;
  }
  kickOf(id: number, now: number): number {
    const r = this.kicks.get(id);
    if (!r) return 0;
    const t = (now - r.at) / 140;
    return t >= 1 ? 0 : r.k * (1 - t);
  }
  /** Where each sniper slug in flight was fired from, for its trail. */
  private slugFrom = new Map<number, { x: number; y: number }>();
  /**
   * Rounds fresh out of a clone's gun: where the barrel was drawn when it
   * fired (x, y), and how far that is from where the server put the round
   * (dx, dy). The round is drawn shifted by that, the shift easing out over
   * PROJ_BLEND ms, so it leaves the barrel this client draws (ours a little
   * ahead of the server, everyone else's a little behind), moving or not.
   */
  private readonly projFrom = new Map<number, { x: number; y: number; dx: number; dy: number; at: number }>();
  private static readonly PROJ_BLEND = 160;

  /**
   * Where to draw projectile i (`now`, performance.now()): its position,
   * shifted onto the drawn barrel while fresh; and how long a streak behind
   * it may be (not back past the barrel it came out of: Infinity for none).
   */
  projDrawn(i: number, now: number, out: { x: number; y: number; trail: number }): { x: number; y: number; trail: number } {
    const p = this.projectiles;
    out.x = p.x[i];
    out.y = p.y[i];
    out.trail = Infinity;
    const f = this.projFrom.size ? this.projFrom.get(p.id[i]) : undefined;
    if (!f) return out;
    const t = (now - f.at) / Game.PROJ_BLEND;
    if (t < 1) {
      const k = (1 - t) * (1 - t);
      out.x += f.dx * k;
      out.y += f.dy * k;
    }
    out.trail = Math.hypot(out.x - f.x, out.y - f.y);
    if (out.trail > 64) this.projFrom.delete(p.id[i]); // (well clear of it: done)
    return out;
  }
  smoothX = 0;
  smoothY = 0;
  /** Prediction errors larger than 0.01 cells seen during reconciliation. */
  corrections = 0;
  private pending: { seq: number; buttons: number; pull: number }[] = [];
  private seq = 0;
  private lastSelf: SelfState | null = null;
  private ack = 0;

  // Remote actors.
  private snaps = new Map<number, Snap[]>();
  // Drop rockets, interpolated like actors.
  private craftSnaps = new Map<number, (CraftState & { tick: number })[]>();
  /**
   * The rocket we are riding in, predicted like our clone: rebased on the
   * server's full-precision state each frame, then stepped over unacked inputs.
   */
  ride: Craft | null = null;
  rideSlot = -1;
  private readonly ridePrev = { x: 0, y: 0, a: 0 };
  rideSmoothX = 0;
  rideSmoothY = 0;
  rideSmoothA = 0;
  /** Rocket prediction errors larger than 0.01 cells seen during reconciliation. */
  craftCorrections = 0;
  private lastSelfCraft: SelfCraftState | null = null;
  // Tanks, interpolated like actors.
  private tankSnaps = new Map<number, (TankState & { tick: number })[]>();
  /** Clones driving a tank right now (they ride hidden inside it). */
  readonly tankPilots = new Set<number>();
  /**
   * The tank we drive, predicted like our clone: rebased on the server's
   * full-precision state each frame, then stepped over unacked inputs. Our
   * clone's body rides in its seat, so the camera and smoothing just work.
   */
  drive: Tank | null = null;
  driveSlot = -1;
  /** Our tank's damage and cannon (HUD), from the latest frame. */
  myTankState: SelfTankState | null = null;
  private lastSelfTank: SelfTankState | null = null;
  /** Tank prediction errors larger than 0.01 cells seen during reconciliation. */
  tankCorrections = 0;
  // Dropships, interpolated like tanks (nobody predicts them: they fly themselves).
  private shipSnaps = new Map<number, (ShipState & { tick: number })[]>();
  private shipsSeenTick = 0;
  private readonly shipPt = { x: 0, y: 0 };
  private readonly rideStep = newCraftStep();
  private readonly ext = { x: 0, y: 0 };
  private clockOffset = NaN; // serverTick - now/TICK_MS
  lastServerTick = 0;
  /** Tick of the frame currently being applied. */
  private frameTick = 0;
  shake = 0;
  hurtFlash = 0;

  // ------------------------------------------------------------ local tick

  /** One fixed 30 Hz client tick: predict own clone, advance local kernels. */
  localTick(buttons: number, aimQ: number, send: (seq: number) => void): void {
    this.geyserSmoke();
    this.seq = (this.seq + 1) & 0xffff;
    send(this.seq);
    copyBody(this.prevBody, this.body);
    // Record every command, even while dead: the server may already have
    // respawned us and will apply it.
    // (Flying our dropship: the controls are the ship's; the clone stands still.)
    if (this.pilot >= 0 || this.rc >= 0) buttons = 0;
    // (Just possessed: lying dead, flat, as the server holds us, till we rise.)
    if (performance.now() - this.possessedAt < (POSSESS_RISE / TICK_RATE) * 1000) buttons = BTN_DOWN;
    // (The digger running: its pull, as the server applies it.)
    const pull = this.alive && !this.drive && this.weapon === WeaponId.Digger && (buttons & BTN_FIRE) !== 0 && this.mob.canFire ? dequantizeAim(aimQ) : NaN;
    this.pending.push({ seq: this.seq, buttons, pull });
    if (this.pending.length > 90) this.pending.shift();
    // The laser's charge, as the server counts it (for the meter and the glow).
    const charging = this.alive && !this.drive && this.weapon === WeaponId.Laser && (buttons & BTN_FIRE) !== 0 && this.reloadLeft === 0 && this.ammo > 0;
    this.laserCharge = charging ? Math.min(LASER_MAX, this.laserCharge + 1) : 0;
    const ridden = this.alive && this.surf >= 0 ? this.tankViews().find((v) => v.slot === this.surf) : undefined;
    const riddenShip = this.alive && this.surfShip >= 0 ? this.shipViews().find((v) => v.slot === this.surfShip) : undefined;
    if (riddenShip) {
      // Riding on a dropship's roof: wherever it flies, on our seat.
      const sp = shipSeat(riddenShip, this.surfSeatNo, { x: 0, y: 0 });
      this.body.x = sp.x - ACTOR_W / 2;
      this.body.y = sp.y - ACTOR_H;
      this.body.vx = riddenShip.vx;
      this.body.vy = riddenShip.vy;
      this.body.onGround = true;
      this.body.jetting = false;
    } else if (ridden) {
      // Riding on top of a vehicle: wherever it is, on our seat.
      const sp = surfSeat(ridden, this.surfSeatNo, { x: 0, y: 0 });
      this.body.x = sp.x - ACTOR_W / 2;
      this.body.y = sp.y - ACTOR_H;
      this.body.vx = ridden.vx;
      this.body.vy = ridden.vy;
      this.body.onGround = true;
      this.body.jetting = false;
    } else if (this.alive && this.drive) {
      stepTank(this.drive, this.terrain, DT, buttons);
      this.seat(this.drive);
      if (this.drive.jetting) this.vehicleJets(this.drive);
    } else if (this.alive) {
      this.body.burdened = this.inv.some((it) => it.weapon === WeaponId.Idol);
      stepBody(this.body, buttons, this.terrain, DT, this.pending[this.pending.length - 1]?.pull ?? NaN);
      if (this.body.jetting) {
        const b = this.body;
        const left = b.vx < 0;
        if (b.cls === ClassId.Droid) droidJets(this.particles, b.x, b.y, b.vx, b.vy);
        else for (let k = 0; k < 3; k++) jetExhaust(this.particles, b.x + (left ? 7 : 0), b.y + ACTOR_H - 5, b.vx, b.vy);
      }
    }
    if (this.ride && !this.alive) {
      const r = this.ride;
      this.ridePrev.x = r.x;
      this.ridePrev.y = r.y;
      this.ridePrev.a = r.a;
      stepCraft(r, this.terrain, DT, this.rideStep, buttons);
      // Bailed out or dropped off: the server takes it from here.
      if (this.rideStep.release) r.passenger = 255;
    }
    for (const [, it] of this.groundItems) stepItem(it, this.terrain, DT);
    // Field first: chunk snapshots and ops applied since the last tick.
    this.field.update();
    this.projectiles.step(this.collider, DT, null, (i, x, y, _a, _d) => {
      if (this.projectiles.kind[i] === ProjKind.Venom) venomSplash(this.particles, x, y);
      else if (PROJ[this.projectiles.kind[i]].ballistic) bulletImpact(this.particles, x, y, this.dustColorAt(x, y));
    });
    const pr = this.projectiles;
    for (let i = 0; i < pr.n; i++) {
      const k = pr.kind[i];
      if (k === ProjKind.Rocket || k === ProjKind.Shell || k === ProjKind.Missile || k === ProjKind.SpiderMissile) rocketTrail(this.particles, pr.x[i], pr.y[i]);
      else if (k === ProjKind.AutoShell) smokeTrail(this.particles, pr.x[i], pr.y[i]);
      else if (k === ProjKind.Plasma) plasmaTrail(this.particles, pr.x[i], pr.y[i], pr.vx[i], pr.vy[i]);
      else if (k === ProjKind.Venom) venomTrail(this.particles, pr.x[i], pr.y[i], pr.vx[i], pr.vy[i]);
      else if (k === ProjKind.FireSpit) fireSpitTrail(this.particles, pr.x[i], pr.y[i], pr.vx[i], pr.vy[i]);
      else if (k === ProjKind.Engine) {
        // Still burning: exhaust out of the nozzle (opposite its heading) while it has fuel, smoke after.
        const burning = PROJ[k].life - pr.life[i] < (PROJ[k].burn ?? 0);
        engineExhaust(this.particles, pr.x[i], pr.y[i], pr.vx[i], pr.vy[i], pr.ang[i], burning);
      }
    }
    for (const [slot, ts] of this.tankSnaps) {
      const t = ts[ts.length - 1];
      if (t && t.jetting && slot !== this.driveSlot) this.vehicleJets(t);
    }
    // Dropship engines: glow and downwash under each pod still attached.
    for (const [, ss] of this.shipSnaps) {
      const s = ss[ss.length - 1];
      if (!s) continue;
      for (let e = 0; e < 4; e++) {
        if (!(s.parts & (1 << (1 + e)))) continue;
        const q = shipPoint(s, ENGINE_X[e], ENGINE_NOZZLE_Y + 1, this.shipPt);
        shipDownwash(this.particles, q.x, q.y, s.vx, s.vy, s.thrust[e]);
      }
    }
    // Drop-rocket exhaust (latest known state; cosmetic plume + local air jet).
    for (const [slot, cs] of this.craftSnaps) {
      const c = slot === this.rideSlot && this.ride ? this.ride : cs[cs.length - 1];
      if (c && c.thrust > 0.12) craftExhaust(this.particles, c.x, c.y, c.a, c.vx, c.vy, c.thrust);
    }
    // Remote jetpacks, and stumps dripping on maimed clones.
    for (const [, s] of this.snaps) {
      const last = s[s.length - 1];
      if (!last || !(last.flags & F_ALIVE)) continue;
      if (last.flags & F_JET) {
        if (classOfFlags(last.flags) === ClassId.Droid) droidJets(this.particles, last.x, last.y, last.vx, last.vy);
        else jetExhaust(this.particles, last.x + (last.vx < 0 ? 7 : 0), last.y + ACTOR_H - 5, last.vx, last.vy);
      }
      if (last.parts !== ALL_PARTS && classOfFlags(last.flags) !== ClassId.Droid) stumpDrip(this.particles, last.x, last.y, last.parts, FACTIONS[last.faction]?.synthetic);
      // A droid with its turret shot off: the torn socket sparks and smokes as it bleeds out.
      if (headless(last.parts, classOfFlags(last.flags))) droidSocket(this.particles, last.x, last.y);
    }
    if (this.alive && this.parts !== ALL_PARTS && this.body.cls !== ClassId.Droid) stumpDrip(this.particles, this.body.x, this.body.y, this.parts, FACTIONS[this.body.faction]?.synthetic);
    if (this.alive && headless(this.parts, this.body.cls)) droidSocket(this.particles, this.body.x, this.body.y);
    this.woundDrips();
    // A dead creature's segments tumbling: gravity, a bounce off the ground, rest; settled a while, worked into the ground.
    for (let k = this.segmentGibs.length - 1; k >= 0; k--) {
      const g = this.segmentGibs[k];
      const resting = Math.hypot(g.vx, g.vy) < 20 && this.terrain.isSolid(Math.floor(g.x), Math.floor(g.y) + 2);
      g.rest = resting ? (g.rest ?? 0) + 1 : 0;
      if (g.rest >= SEGMENT_BURY) {
        this.buriedSegments.push({ x: g.x, y: g.y, a: g.a, kind: g.kind, seed: g.seed, i: g.i });
        this.segmentGibs.splice(k, 1);
        continue;
      }
      if (--g.life <= 0) {
        this.segmentGibs.splice(k, 1);
        continue;
      }
      g.vy += GRAVITY * DT;
      const nx = g.x + g.vx * DT;
      const ny = g.y + g.vy * DT;
      if (this.terrain.isSolid(Math.floor(nx), Math.floor(ny))) {
        if (this.terrain.isSolid(Math.floor(g.x), Math.floor(ny))) {
          g.vy *= -0.35;
          g.vx *= 0.6;
        } else g.vx *= -0.4;
        g.va *= 0.5;
      } else {
        g.x = nx;
        g.y = ny;
      }
      g.a += g.va * DT;
    }
    // Venom in the blood: it weeps out of them.
    for (const [id, amount] of this.venomIn) {
      if (id === this.myId) {
        if (this.alive) venomDrip(this.particles, this.body.x + 4, this.body.y + 7, amount);
        continue;
      }
      const s = this.snaps.get(id);
      const last = s?.[s.length - 1];
      if (last && last.flags & F_ALIVE) venomDrip(this.particles, last.x + 4, last.y + 7, amount);
    }
    // Coral growing out of limbs: it sheds spores, the more grown the more.
    for (const [id, levels] of this.coralIn) {
      const m = Math.max(...levels);
      if (id === this.myId) {
        if (this.alive) coralSpores(this.particles, this.body.x + 4, this.body.y + 7, m);
        continue;
      }
      const s = this.snaps.get(id);
      const last = s?.[s.length - 1];
      if (last && last.flags & F_ALIVE) coralSpores(this.particles, last.x + 4, last.y + 7, m);
    }
    if (this.sfx) this.soundBeds(this.sfx);
    // Bodies in the engine so shrapnel stops in them and grains bounce off
    // them on screen too; only the server's results (damage, knockback) count.
    const actors = this.bodyField;
    actors.clear();
    if (this.alive) actors.add(this.myId, this.body.x, this.body.y, this.body.vx, this.body.vy);
    for (const [id, s] of this.snaps) {
      const last = s[s.length - 1];
      if (last && last.flags & F_ALIVE && actors.n < MAX_ACTORS) actors.add(id, last.x, last.y, last.vx, last.vy);
    }
    for (const [slot, cs] of this.craftSnaps) {
      const c = slot === this.rideSlot && this.ride ? this.ride : cs[cs.length - 1];
      if (!c || actors.n >= MAX_ACTORS) continue;
      const e = craftHalfExtents(c.a, this.ext);
      actors.add(128 + slot, c.x - e.x, c.y - e.y, c.vx, c.vy, 2 * e.x, 2 * e.y, 60, 255, 0);
    }
    for (const [slot, ts] of this.tankSnaps) {
      const t = slot === this.driveSlot && this.drive ? this.drive : ts[ts.length - 1];
      if (t && actors.n < MAX_ACTORS) actors.add(192 + slot, t.x, t.y, t.vx, t.vy, tankW(t), tankH(t), 200 * (t.s ?? 1), 255, 0.3);
    }
    for (const [slot, ss] of this.shipSnaps) {
      const s = ss[ss.length - 1];
      if (s && actors.n < MAX_ACTORS) actors.add(196 + slot, s.x, s.y, s.vx, s.vy, SHIP_W, SHIP_H, 120, 255, 0.2);
    }
    this.stoneTick();
    this.particles.step(this.collider, DT, this.particleHooks, actors);
    this.shake *= 0.85;
    this.hurtFlash *= 0.9;
  }

  // ------------------------------------------------------------ network

  applyFrame(tick: number, ack: number, r: Reader): void {
    this.ack = ack;
    this.frameTick = tick;
    this.lastSelf = null;
    this.lastSelfCraft = null;
    this.lastSelfTank = null;
    applyFrameRecords(r, this.terrain, this);
    // (Repainted on the server's schedule, from the ground as it is now: debris settling in it, a cave-in.)
    if (this.waterBodies.length && tick % WATER_TICKS === 0) paintWater(this.terrain, this.waterBodies);
    if (this.tanksSeenTick !== tick) {
      this.tankSnaps.clear();
      this.tankPilots.clear();
    }
    if (this.shipsSeenTick !== tick) this.shipSnaps.clear();
    // No rocket record in this frame means no drop rockets near us.
    if (this.craftsSeenTick !== tick) this.craftSnaps.clear();
    this.lastServerTick = tick;
    const est = tick - performance.now() / TICK_MS;
    if (Number.isNaN(this.clockOffset) || Math.abs(est - this.clockOffset) > 10) this.clockOffset = est;
    else this.clockOffset = this.clockOffset * 0.95 + est * 0.05;
    // Reconcile after the whole frame so replay sees this tick's terrain.
    if (this.lastSelf) this.reconcile(this.lastSelf);
    this.myTankState = this.alive ? this.lastSelfTank : null;
    if (this.lastSelfTank && this.alive) this.reconcileTank(this.lastSelfTank);
    else {
      this.drive = null;
      this.driveSlot = -1;
    }
    if (this.lastSelfCraft && !this.alive) this.reconcileCraft(this.lastSelfCraft);
    else {
      this.ride = null;
      this.rideSlot = -1;
    }
  }

  /** Put our clone in the driver's seat. */
  private seat(t: Tank): void {
    const b = this.body;
    b.x = t.x + SEAT_X;
    b.y = t.y + SEAT_Y;
    b.vx = t.vx;
    b.vy = t.vy;
    b.onGround = t.onGround;
    b.jetting = false;
  }

  /** Rebase the predicted tank on the server's state and replay unacked inputs. */
  private reconcileTank(s: SelfTankState): void {
    const fresh = !this.drive || this.driveSlot !== s.slot;
    if (!this.drive || fresh) this.drive = newTank(s.x, s.y);
    const d = this.drive;
    this.driveSlot = s.slot;
    const b = this.body;
    const rawX = d.x;
    const rawY = d.y;
    const oldX = b.x + this.smoothX;
    const oldY = b.y + this.smoothY;
    d.x = s.x;
    d.y = s.y;
    d.vx = s.vx;
    d.vy = s.vy;
    d.fuel = s.fuel;
    d.chute = s.chute;
    d.onGround = s.onGround;
    d.jetting = s.jetting;
    d.parts = s.parts;
    d.a = s.a;
    d.w = s.w;
    d.pilot = this.myId;
    // Its size and kind from the last we saw of it (a mole is smaller than a tank).
    const seen = this.tankSnaps.get(s.slot);
    const last = seen?.[seen.length - 1];
    if (last) {
      d.kind = last.kind;
      d.s = last.s;
    }
    // pending was already trimmed to unacked commands by reconcile().
    for (const p of this.pending) stepTank(d, this.terrain, DT, p.buttons);
    this.seat(d);
    if (!fresh && Math.abs(rawX - d.x) + Math.abs(rawY - d.y) > 0.01) this.tankCorrections++;
    const ex = oldX - b.x;
    const ey = oldY - b.y;
    if (!fresh && ex * ex + ey * ey < 48 * 48) {
      this.smoothX = ex;
      this.smoothY = ey;
    } else {
      this.smoothX = this.smoothY = 0;
      copyBody(this.prevBody, b);
    }
  }

  /** Rebase the predicted rocket on the server's state and replay unacked inputs. */
  private reconcileCraft(s: SelfCraftState): void {
    const fresh = !this.ride || this.rideSlot !== s.slot;
    if (!this.ride || fresh) this.ride = newCraft(s.x, this.myId);
    const r = this.ride;
    this.rideSlot = s.slot;
    const rawX = r.x;
    const rawY = r.y;
    const oldX = r.x + this.rideSmoothX;
    const oldY = r.y + this.rideSmoothY;
    const oldA = r.a + this.rideSmoothA;
    r.x = s.x;
    r.y = s.y;
    r.vx = s.vx;
    r.vy = s.vy;
    r.a = s.a;
    r.w = s.w;
    r.targetX = s.targetX;
    r.timer = s.timer;
    r.phase = s.phase;
    r.parts = s.parts;
    r.prevButtons = s.prevButtons;
    for (let i = 0; i < s.partHp.length; i++) r.partHp[i] = s.partHp[i];
    r.hp = s.partHp[0];
    r.passenger = this.myId;
    r.delivered = 255;
    // pending was already trimmed to unacked commands by reconcile().
    for (const p of this.pending) {
      stepCraft(r, this.terrain, DT, this.rideStep, p.buttons);
      if (this.rideStep.release) {
        r.passenger = 255;
        break;
      }
    }
    const ex = oldX - r.x;
    const ey = oldY - r.y;
    if (!fresh && Math.abs(rawX - r.x) + Math.abs(rawY - r.y) > 0.01) this.craftCorrections++;
    if (!fresh && ex * ex + ey * ey < 48 * 48) {
      this.rideSmoothX = ex;
      this.rideSmoothY = ey;
      this.rideSmoothA = wrapAngle(oldA - r.a);
    } else {
      this.rideSmoothX = this.rideSmoothY = this.rideSmoothA = 0;
      this.ridePrev.x = r.x;
      this.ridePrev.y = r.y;
      this.ridePrev.a = r.a;
    }
  }

  private reconcile(s: SelfState): void {
    const wasAlive = this.alive;
    this.alive = (s.flags & F_ALIVE) !== 0;
    // Fragged means we were alive and now we're not; never on yet (loading in, waiting to deploy) isn't that.
    if (wasAlive && !this.alive) this.fragged = true;
    if (!this.alive) this.wounds.clear(this.myId);
    else if (this.alive) this.fragged = false;
    this.hp = s.hp;
    this.respawnTicks = s.respawn;
    this.parts = s.parts;
    this.partHp = s.partHp;
    this.cooldown = s.cooldown;
    this.inv = s.inv;
    this.spectate = s.spectate;
    if (s.invVersion !== this.invVersion) {
      this.invVersion = s.invVersion;
      this.slot = s.slot;
    }
    if (this.slot >= this.inv.length) this.slot = Math.max(0, this.inv.length - 1);
    this.reloadLeft = s.reload;
    this.gold = s.gold;
    const b = this.body;
    const ack = this.ack;
    this.pending = this.pending.filter((p) => seqNewer(p.seq, ack));
    const rawX = b.x;
    const rawY = b.y;
    const oldX = b.x + this.smoothX;
    const oldY = b.y + this.smoothY;
    b.x = s.x;
    b.y = s.y;
    b.vx = s.vx;
    b.vy = s.vy;
    b.fuel = s.fuel;
    b.onGround = (s.flags & F_GROUND) !== 0;
    b.jetting = (s.flags & F_JET) !== 0;
    // Predict with the body we actually have left (legs, jetpack).
    mobility(s.parts, this.mob, classOfFlags(s.flags));
    b.legs = this.mob.legs;
    b.jet = this.mob.jet;
    b.crawl = this.possessedIds.has(this.myId);
    b.cls = classOfFlags(s.flags);
    b.stance = s.stance;
    b.downTicks = s.downTicks;
    b.faction = s.faction;
    if (!this.alive) {
      copyBody(this.prevBody, b);
      this.smoothX = this.smoothY = 0;
      return;
    }
    if (this.surf >= 0 || this.surfShip >= 0) {
      // Surfing: the vehicle carries us (localTick seats us on it).
      b.x = rawX;
      b.y = rawY;
      return;
    }
    if (this.lastSelfTank) {
      // Driving: the clone rides in the seat; reconcileTank rebases the tank.
      b.x = rawX;
      b.y = rawY;
      return;
    }
    b.burdened = this.inv.some((it) => it.weapon === WeaponId.Idol);
    for (const p of this.pending) stepBody(b, p.buttons, this.terrain, DT, p.pull);
    // Hide small corrections by easing the visual offset back to zero.
    const ex = oldX - b.x;
    const ey = oldY - b.y;
    if (wasAlive && Math.abs(rawX - b.x) + Math.abs(rawY - b.y) > 0.01) this.corrections++;
    if (wasAlive && ex * ex + ey * ey < 48 * 48) {
      this.smoothX = ex;
      this.smoothY = ey;
    } else {
      this.smoothX = this.smoothY = 0;
      copyBody(this.prevBody, b);
    }
  }

  /** Fractional server tick we render remote actors at. */
  renderTick(): number {
    return performance.now() / TICK_MS + this.clockOffset - INTERP_TICKS;
  }

  /**
   * Who rides on top of which vehicle (tank and dropship surfing), from the
   * latest word on the vehicles: player id -> slot | seat << 8 (| SHIP_RIDE
   * for a dropship).
   */
  private ridingNow(): Map<number, number> {
    const out = new Map<number, number>();
    for (const [slot, s] of this.tankSnaps) for (const r of s[s.length - 1].riders) out.set(r & 255, slot | (r & 0xff00));
    for (const [slot, s] of this.shipSnaps) for (const r of s[s.length - 1].riders) out.set(r & 255, slot | (r & 0xff00) | SHIP_RIDE);
    return out;
  }

  /**
   * Where a rider stands (its body's top-left) on its vehicle as that's
   * drawn this frame. Riders are drawn from the vehicle, not from their own
   * snapshots: those are interpolated separately (and the vehicle we drive
   * is predicted, ahead of them), so riders drawn on their own would slide
   * and stutter about the deck.
   */
  private seatOn(views: TankView[], slot: number, seat: number): { x: number; y: number } | null {
    const t = views.find((v) => v.slot === slot);
    if (!t) return null;
    const sp = surfSeat(t, seat, { x: 0, y: 0 });
    return { x: sp.x - ACTOR_W / 2, y: sp.y - ACTOR_H };
  }

  /** The same on a dropship's roof. */
  private seatOnShip(ships: ShipView[], slot: number, seat: number): { x: number; y: number } | null {
    const sh = ships.find((v) => v.slot === slot);
    if (!sh) return null;
    const sp = shipSeat(sh, seat, { x: 0, y: 0 });
    return { x: sp.x - ACTOR_W / 2, y: sp.y - ACTOR_H };
  }

  /** Our own clone, riding on top of a vehicle: where it stands on the vehicle as drawn (by `alpha`), or null when not riding. */
  ridingAt(alpha = 1): { x: number; y: number } | null {
    if (!this.alive) return null;
    if (this.surfShip >= 0) return this.seatOnShip(this.shipViews(), this.surfShip, this.surfSeatNo);
    if (this.surf < 0) return null;
    return this.seatOn(this.tankViews(alpha), this.surf, this.surfSeatNo);
  }

  remoteViews(alpha = 1): RemoteView[] {
    const rt = this.renderTick();
    const out: RemoteView[] = [];
    const riding = this.ridingNow();
    const views = riding.size ? this.tankViews(alpha) : [];
    const ships = riding.size ? this.shipViews() : [];
    for (const [id, s] of this.snaps) {
      if (s.length === 0) continue;
      let a = s[0];
      let b = s[0];
      for (let i = s.length - 1; i >= 0; i--) {
        if (s[i].tick <= rt) {
          a = s[i];
          b = s[Math.min(i + 1, s.length - 1)];
          break;
        }
      }
      let x: number;
      let y: number;
      if (a === b) {
        // Extrapolate briefly past the newest snapshot.
        const dt = Math.max(-INTERP_TICKS, Math.min(3, rt - a.tick)) / TICK_RATE;
        x = a.x + a.vx * Math.max(0, dt);
        y = a.y + a.vy * Math.max(0, dt);
      } else {
        const t = (rt - a.tick) / (b.tick - a.tick);
        x = a.x + (b.x - a.x) * t;
        y = a.y + (b.y - a.y) * t;
      }
      // Teleports (respawn) should not interpolate across the map.
      if (Math.abs(b.x - a.x) > 64 || Math.abs(b.y - a.y) > 64) {
        x = b.x;
        y = b.y;
      }
      // On top of a vehicle: on its seat, wherever the vehicle is drawn (and standing, not walking).
      const ride = riding.get(id);
      const seat =
        ride === undefined || !(b.flags & F_ALIVE)
          ? null
          : ride & SHIP_RIDE
            ? this.seatOnShip(ships, ride & 255, (ride >> 8) & 255)
            : this.seatOn(views, ride & 255, (ride >> 8) & 255);
      if (seat) {
        x = seat.x;
        y = seat.y;
      }
      out.push({ id, x, y, aim: b.aim, flags: b.flags, hp: b.hp, weapon: b.weapon, moving: !seat && Math.abs(b.vx) > 5, parts: b.parts, stance: b.stance, faction: b.faction, vx: b.vx, vy: b.vy });
    }
    return out;
  }

  // ------------------------------------------------------------ FrameHandler

  /** The dropship slot we're remote-piloting, or -1 (our clone stands inert meanwhile). */
  pilot = -1;
  /** The watchdog (tank slot) we're driving by remote, or -1 (the clone stands inert meanwhile too). */
  rc = -1;
  /** Tank surfing: the vehicle (tank slot) we ride on top of, or -1, and our seat on it. */
  surf = -1;
  surfSeatNo = 0;
  /** Dropship surfing: the dropship (ship slot) we ride on the roof of, or -1 (our seat is surfSeatNo). */
  surfShip = -1;

  self(s: SelfState): void {
    this.lastSelf = s;
    this.pilot = s.pilot === 255 ? -1 : s.pilot;
    this.rc = s.rc === 255 ? -1 : s.rc;
    // (slot | seat << 4, the top bit set for a dropship; 255 when not riding.)
    const ship = s.surf !== 255 && (s.surf & 128) !== 0;
    this.surf = s.surf === 255 || ship ? -1 : s.surf & 15;
    this.surfShip = ship ? s.surf & 15 : -1;
    this.surfSeatNo = s.surf === 255 ? 0 : (s.surf >> 4) & 7;
  }

  /** The watchdog we're driving by remote, if we are. */
  remoteDog(): TankView | null {
    if (this.rc < 0) return null;
    return this.tankViews().find((v) => v.slot === this.rc) ?? null;
  }

  /** Our own watchdog, if we have one out. */
  myDog(): TankView | null {
    return this.tankViews().find((v) => isDog(v) && v.owner === this.myId) ?? null;
  }

  /**
   * The radio's menu, in order: what we can call in (a watchdog or a
   * tarantula only while we haven't one out), then the remote when we've
   * a drone to take over. Shared by the menu drawn and the selector that
   * scrolls it.
   */
  radioEntries(): RadioEntry[] {
    const out: RadioEntry[] = [
      { kind: CallKind.Dropship, name: 'DROPSHIP', blurb: 'air support · 2 turrets · 8 bombs · 3 ride its roof', cost: CALL_COST },
      { kind: CallKind.Tank, name: 'TANK', blurb: 'parachuted onto your position', cost: CALL_COST },
      { kind: CallKind.Mole, name: 'MOLE', blurb: 'small tank · plasma flamer digs · frill shields 3 riders', cost: MOLE_COST },
    ];
    if (!this.myDog()) out.push({ kind: CallKind.Watchdog, name: 'WATCHDOG', blurb: 'small robot tank · guards you · drive it (P)', cost: WATCHDOG_COST });
    if (!this.mySpider()) out.push({ kind: CallKind.Tarantula, name: 'TARANTULA', blurb: 'ultraheavy spider · missiles + laser · guards you', cost: TARANTULA_COST });
    const drones = this.remotes();
    if (drones.length) {
      const next = drones[this.remoteIndex() + 1];
      out.push({ kind: CallKind.Pilot, name: next ? `REMOTE: ${next.name}` : 'REMOTE: BACK TO YOU', blurb: `${drones.length} drone${drones.length > 1 ? 's' : ''} · P steps through them`, cost: 0, free: true });
    }
    return out;
  }

  /** The radio's highlighted entry (radioEntries index): scrolled by aim, wheel or hover; fire calls it. */
  radioSel = 0;

  /**
   * Our drones the remote can take over, in the server's order (World.remotesOf):
   * our dropships, then our watchdogs, then our tarantulas, each by slot.
   */
  remotes(): { ship: boolean; slot: number; name: string }[] {
    const out: { ship: boolean; slot: number; name: string }[] = [];
    for (const v of [...this.shipViews()].sort((a, b) => a.slot - b.slot)) if (v.owner === this.myId && !v.leaving) out.push({ ship: true, slot: v.slot, name: 'DROPSHIP' });
    const pets = [...this.tankViews()].sort((a, b) => a.slot - b.slot).filter((v) => isPet(v) && v.owner === this.myId && !v.chute);
    for (const v of pets) if (isDog(v)) out.push({ ship: false, slot: v.slot, name: 'WATCHDOG' });
    for (const v of pets) if (isSpider(v)) out.push({ ship: false, slot: v.slot, name: 'TARANTULA' });
    return out;
  }

  /** Which of remotes() we're driving now, or -1 (ourselves). */
  remoteIndex(): number {
    return this.remotes().findIndex((r) => (r.ship ? r.slot === this.pilot : r.slot === this.rc));
  }

  /** Our own tarantula, if we have one out. */
  mySpider(): TankView | null {
    return this.tankViews().find((v) => isSpider(v) && v.owner === this.myId) ?? null;
  }

  /** Sticky charges about (R_CHARGES). */
  chargeList: ChargeState[] = [];
  /** Stoneburner beacons and molten cores about (R_BURNERS). */
  burnerList: BurnerState[] = [];
  footList: FootState[] = [];
  /** Stoneburner strikes seen: where, when (performance.now ms), and their seed (its fireball, then the plume off its core, for CLOUD_MS). */
  readonly stoneBurns: { x: number; y: number; at: number; seed: number }[] = [];
  /** When the last strike's flash went off (performance.now ms; the screen whites out, then fades). */
  stoneFlashAt = -1e9;
  /** The ground still shaking after a strike (a long rumble under the quick jolts of `shake`). */
  quake = 0;
  /** A strike's ejecta: burning chunks thrown high, trailing fire and smoke till they come down (just for show). */
  private readonly ejecta: { x: number; y: number; vx: number; vy: number; life: number }[] = [];

  /** When we were possessed (performance.now ms): we lie dead a moment (POSSESS_RISE), then rise. */
  possessedAt = -1e9;
  /** Clones a coral crab has: risen again possessed, on the coral's side against everyone else (R_POSSESSED). */
  possessedIds = new Set<number>();
  /** The few of them still able to use their guns (the rest lunge and vomit). */
  armedPossessed = new Set<number>();
  /** Is clone `id` one of the possessed without its guns (holding `weapon`: the digger it can still use)? */
  clawsOnly(id: number, weapon: number): boolean {
    return this.possessedIds.has(id) && !this.armedPossessed.has(id) && weapon !== WeaponId.Digger;
  }
  /** A possessed clone vomits the crab's blood (R_VOMIT): the gout, and its retching. */
  vomit(_id: number, x: number, y: number, aim: number): void {
    bileVomit(this.particles, x, y, aim);
    this.sfx?.hiss(x, y, true);
  }

  /** Who's possessed now: a feed line for each newly taken (and for us, a jolt). */
  possessed(ids: number[], armed: number[] = []): void {
    this.armedPossessed = new Set(armed);
    const now = performance.now();
    const next = new Set(ids);
    for (const id of next) {
      if (this.possessedIds.has(id)) continue;
      const name = this.players.get(id)?.name ?? '???';
      const me = id === this.myId;
      // It dies where it stands, horribly: blood, the crab's blue, coral bursting out of it. It lies there, then rises.
      const at = me ? { x: this.body.x, y: this.body.y } : this.remoteViews().find((r) => r.id === id);
      if (at) {
        const cx = at.x + ACTOR_W / 2;
        const cy = at.y + ACTOR_H / 2;
        bloodSplat(this.particles, cx, cy, 46, 170);
        crabBloodSplat(this.particles, cx, cy - 3, 24, 130);
        coralBurst(this.particles, cx, cy - 2);
        this.sfx?.gib(cx, cy, 1.4, false);
      }
      if (me) this.possessedAt = now;
      this.feed.push({ text: me ? 'You were possessed by a coral crab' : `${name} was possessed by a coral crab`, color: me ? '#ff6f9a' : '#ff9ab8', at: now, kill: true });
      if (this.feed.length > 6) this.feed.shift();
      if (me) this.shake = Math.max(this.shake, 6);
    }
    this.possessedIds = next;
  }

  /** The sticky charges about: each stuck one beeps as its light comes on (any tick of its countdown since we last heard of it). */
  charges(list: ChargeState[]): void {
    for (const c of list) {
      if (!c.stuck) continue;
      let prev: ChargeState | null = null;
      let best = 30;
      for (const o of this.chargeList) {
        const d = Math.hypot(o.x - c.x, o.y - c.y);
        if (o.stuck && d < best && o.fuse >= c.fuse) {
          best = d;
          prev = o;
        }
      }
      const from = prev ? prev.fuse - 1 : c.fuse;
      for (let f = from; f >= c.fuse && from - f < 30; f--) {
        if (stickyBeepAt(f)) {
          this.sfx?.beep(c.x, c.y, 1 - f / STICKY_FUSE);
          break;
        }
      }
    }
    this.chargeList = list;
  }

  burners(beacons: BurnerState[], feet: FootState[]): void {
    // A beacon beeps each time its light comes on: any tick of its countdown since we last heard of it
    // (matched to the one nearest where it was) that its light came on in.
    for (const b of beacons) {
      if (b.phase === BurnerPhase.Strike) continue;
      let prev: BurnerState | null = null;
      let best = 40;
      for (const o of this.burnerList) {
        const d = Math.hypot(o.x - b.x, o.y - b.y);
        if (d < best && o.fuse >= b.fuse) {
          best = d;
          prev = o;
        }
      }
      const from = prev ? prev.fuse - 1 : b.fuse;
      for (let f = from; f >= b.fuse && f > 0 && from - f < 30; f--) {
        if (beepAt(f)) {
          this.sfx?.beep(b.x, b.y, 1 - f / BURNER_FUSE);
          break;
        }
      }
    }
    // The missile on its way: the scream of it, once.
    for (const b of beacons) if (b.phase === BurnerPhase.Strike && !this.burnerList.some((o) => o.phase === BurnerPhase.Strike && Math.hypot(o.x - b.x, o.y - b.y) < 40)) this.sfx?.incoming(b.x, b.y);
    this.burnerList = beacons;
    this.footList = feet;
    // A blob flaring this tick: its burst, once (the server burnt whatever it reached just now).
    for (const f of feet) if (f.flare) coreFlare(this.particles, f.x, f.y, f.life / FOOT_LIFE);
  }

  /**
   * A Stoneburner strike lands: the whole room sees it. The world whites
   * out and fades back to the fireball, a dome of fire swelling up over the
   * site and gone in seconds (the plume of smoke off its molten core rising
   * long after), everything shakes (worst near it, but everyone feels it),
   * and the roar of it reaches everyone.
   */
  stoneBurn(x: number, y: number, _by: number, seed: number): void {
    const now = performance.now();
    this.stoneFlashAt = now;
    this.stoneBurns.push({ x, y, at: now, seed });
    if (this.stoneBurns.length > 4) this.stoneBurns.shift();
    stoneburnBlast(this.particles, x, y, BLAST_IMPULSE);
    for (let k = 0; k < 44; k++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
      const sp = 380 + Math.random() * 900;
      this.ejecta.push({ x: x + (Math.random() - 0.5) * 30, y: y - 8, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 60 + Math.floor(Math.random() * 90) });
    }
    this.flashes.push({ x, y, r: 260, at: now });
    const me = this.body;
    const d = Math.hypot(me.x - x, me.y - y);
    this.shake = Math.max(this.shake, 14 + Math.max(0, 1 - d / 1500) * 26);
    this.quake = Math.max(this.quake, 4 + Math.max(0, 1 - d / 2500) * 12);
    this.sfx?.stoneburn(x, y);
  }

  /** Every tick: the rumble dying away, and the molten cores burning (flame, smoke, sparks off them). */
  private stoneTick(): void {
    if (this.quake > 0.05) {
      this.shake = Math.max(this.shake, this.quake);
      this.quake *= 0.985;
    } else this.quake = 0;
    for (const f of this.footList) moltenCore(this.particles, f.x, f.y, f.life / FOOT_LIFE);
    // The plume: smoke rising off each strike's molten core as it bores down (thinning as it cools).
    const now = performance.now();
    for (const sb of this.stoneBurns) {
      const age = (now - sb.at) / CLOUD_MS;
      if (age < 0 || age >= 1) continue;
      // (The core: where its sludge is, pooled and sinking in the crater; else the crater itself.)
      let cx = 0;
      let cy = 0;
      let n = 0;
      for (const f of this.footList) {
        if (Math.abs(f.x - sb.x) > 220) continue;
        cx += f.x;
        cy = Math.max(cy, f.y);
        n++;
      }
      stonePlume(this.particles, n ? cx / n : sb.x, n ? cy : sb.y, 1 - age);
    }
    // The ejecta: falling, trailing fire and then smoke as they cool, till they hit the ground.
    for (let i = this.ejecta.length - 1; i >= 0; i--) {
      const e = this.ejecta[i];
      e.vy += GRAVITY * DT;
      e.vx *= 0.995;
      e.x += e.vx * DT;
      e.y += e.vy * DT;
      const hot = Math.min(1, e.life / 70);
      if (--e.life <= 0 || this.terrain.isSolid(Math.floor(e.x), Math.floor(e.y)) || e.y > WORLD_H) {
        for (let k = 0; k < 6; k++) this.particles.spawn(PK.Spark, e.x, e.y, (Math.random() - 0.5) * 300, -Math.random() * 220, 10 + Math.random() * 8);
        this.ejecta.splice(i, 1);
        continue;
      }
      if (hot > 0.25) this.particles.spawn(PK.Flame, e.x, e.y, e.vx * 0.05, e.vy * 0.05, 6 + 10 * hot);
      this.particles.spawn(PK.Smoke, e.x, e.y, e.vx * 0.02, e.vy * 0.02, 50 + 70 * (1 - hot));
    }
  }

  /** Landmines on the map (R_MINES). */
  mineList: MineState[] = [];
  mines(list: MineState[]): void {
    this.mineList = list;
  }

  /** The map's geysers (R_GEYSERS), and when each last blew (performance.now ms; its deadly cloud shows for GEYSER_TOXIC_TICKS after). */
  geyserList: GeyserState[] = [];
  readonly geyserBlownAt = new Map<number, number>();

  geysers(list: GeyserState[]): void {
    this.geyserList = list;
  }

  /**
   * The geysers' smoke, once a tick, as real smoke particles (the guns'
   * kind), for the ones near enough to see: a lazy wisp of steam off a quiet
   * vent; thick, fast smoke and sparks off a rumbling one; and over a blown
   * one, its deadly cloud: yellow-green smoke filling the very space where
   * the server's smoke does its harm (GEYSER_CLOUD_R round geyserCloud).
   */
  private geyserSmoke(): void {
    const now = performance.now();
    const p = this.particles;
    const r = (a: number, b: number) => a + Math.random() * (b - a);
    this.geyserList.forEach((g, i) => {
      if (g.flags & GF_DEAD || Math.abs(g.x - this.body.x) > 700 || Math.abs(g.y - this.body.y) > 450) return;
      if (g.flags & GF_RUMBLE) {
        for (let k = 0; k < 3; k++) p.spawn(PK.Smoke, g.x + r(-2, 2), g.y - 1, r(-25, 25), r(-140, -70), r(40, 80));
        if (Math.random() < 0.5) p.spawn(PK.Spark, g.x + r(-1, 1), g.y - 1, r(-40, 40), r(-180, -90), r(8, 16));
      } else if (Math.random() < 0.55) {
        p.spawn(PK.Smoke, g.x + r(-1, 1), g.y - 1, r(-8, 8), r(-45, -20), r(50, 90));
      }
      if (g.flags & GF_TOXIC) {
        const at = this.geyserBlownAt.get(i) ?? now;
        const age = Math.min(GEYSER_TOXIC_TICKS, ((now - at) / 1000) * TICK_RATE);
        const c = geyserCloud(g.x, g.y, age);
        for (let k = 0; k < 7; k++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.sqrt(Math.random());
          p.spawn(PK.Smoke, c.x + Math.cos(a) * d * GEYSER_CLOUD_R, c.y + Math.sin(a) * d * GEYSER_CLOUD_R * 1.3, r(-12, 12), r(-14, 4), r(25, 45), 0, Math.random() < 0.7 ? 0xa8c838 : 0xd8e060);
        }
      }
    });
  }

  /**
   * The flora's state: every plant that's just gone is torn to pieces where
   * it stood (in its own colours), and a blister coral bursts in a spray of
   * caustic sap.
   */
  floraState(dead: Uint8Array, swelling: number[]): void {
    if (dead.length !== this.flora.length) return; // (not this map's)
    for (let i = 0; i < dead.length; i++) {
      if (!dead[i] || this.floraDead[i]) continue;
      const f = this.flora[i];
      if (Math.abs(f.x - this.body.x) > 900 || Math.abs(f.y - this.body.y) > 600) continue;
      const b = floraBox(f);
      const colors = floraColors(f);
      floraGibs(this.particles, f.kind, f.x, b.y0, b.y1, colors, f.kind === FloraKind.Hanging);
      if (f.kind === FloraKind.Blister) {
        blisterBurst(this.particles, f.x, f.y - 8, colors[2]);
        this.flashes.push({ x: f.x, y: f.y - 8, r: 18, at: performance.now() });
        this.sfx?.explode(f.x, f.y - 8, 8);
      }
    }
    this.floraDead = dead;
    this.floraSwell = new Set(swelling);
  }

  /** Standing water: the server's levels; the same region painted into our terrain (the predictor swims in it too). */
  water(bodies: { x: number; y: number; level: number; volume: number }[]): void {
    this.waterBodies = bodies;
    paintWater(this.terrain, bodies);
  }
  private waterBodies: { x: number; y: number; level: number; volume: number }[] = [];

  /**
   * Wildlife in view (R_BEASTS): each creature's body points as last sent
   * and the frame before, and its recent states by server tick (`snaps`), to
   * draw it at the same render time as everything else (beastPose).
   */
  readonly beastViews = new Map<number, BeastView>();
  /** Wildlife that dropped out of the last R_BEASTS (killed, most likely: its R_BEAST_DIE follows in the same frame). */
  private readonly beastsGone = new Map<number, BeastView>();
  /** Scratch for beastPose. */
  private readonly posePts: number[] = [];
  /**
   * A dead millipede's segments, each flung off whole (its own camouflage
   * and all), tumbling, bouncing, coming to rest and fading: where, how it's
   * turned and spinning, which creature (seed) and segment it was, life left (ticks).
   */
  readonly segmentGibs: { x: number; y: number; vx: number; vy: number; a: number; va: number; kind: number; seed: number; i: number; life: number; rest?: number }[] = [];
  private buzzAt = -1e9;
  /** When each dragon last rattled (by slot, ms). */
  private readonly rattleAt = new Map<number, number>();
  beasts(list: BeastState[]): void {
    const seen = new Set<number>();
    this.beastsGone.clear();
    for (const b of list) {
      seen.add(b.slot);
      const v = this.beastViews.get(b.slot);
      if (b.same) {
        // Just as it was: another snapshot of it standing still.
        if (v) {
          v.prev = v.cur;
          v.prevLift = v.lift;
          pushBeastSnap(v, this.frameTick);
        }
        continue;
      }
      if (v && v.kind === b.kind && v.seed === b.seed && v.cur.length === b.pts.length) {
        v.prev = v.cur;
        v.cur = b.pts;
        v.prevLift = v.lift;
        // Rearing up: it hisses.
        if (v.lift < 0.05 && b.lift >= 0.05) {
          // (A dragon's jaws flung wide: it roars, and spits its brood.)
          if (b.kind === BeastKind.Dragon) this.sfx?.roar(b.pts[0], b.pts[1]);
          else this.sfx?.hiss(b.pts[0], b.pts[1], b.kind === 0);
        }
        v.lift = b.lift;
        // An asp hurt: its goo spatters (more, the harder it was hit).
        if (b.kind === BeastKind.Spider && b.hp < v.hp) ichorSplat(this.particles, b.pts[0], b.pts[1], Math.min(20, 4 + Math.round((v.hp - b.hp) / 4)), 70);
        // Any creature hurt: its blue blood (more, the harder it was hit).
        if (b.hp < v.hp && b.kind !== BeastKind.Nest && b.kind !== BeastKind.Web) crabBloodSplat(this.particles, b.pts[0], b.pts[1], Math.min(24, 3 + Math.round((v.hp - b.hp) / 5)), 80);
        if (b.kind === BeastKind.Asp && b.hp < v.hp) gooSplat(this.particles, b.pts[0], b.pts[1], b.seed, Math.min(20, 4 + Math.round((v.hp - b.hp) / 4)), 70);
        v.hp = b.hp;
        v.top = Math.max(v.top, b.hp);
        v.attached = b.attached;
        v.frozen = b.frozen;
        // A cave spider grabbing a clone in its jaws: a hiss.
        if (b.kind === BeastKind.Spider && !v.coiled && b.coiled) this.sfx?.hiss(b.pts[0], b.pts[1], false);
        v.coiled = b.coiled;
        v.gem = b.gem;
        // A basilisk woken: its roar.
        if (!v.frenzy && b.frenzy) this.sfx?.roar(b.pts[0], b.pts[1]);
        // A dragon awake: its rattle, every couple of seconds, from its tail.
        if (b.kind === BeastKind.Dragon && b.frenzy && !b.frozen && performance.now() - (this.rattleAt.get(b.slot) ?? -1e9) > 2200) {
          this.rattleAt.set(b.slot, performance.now());
          this.sfx?.rattle(b.pts[b.pts.length - 2], b.pts[b.pts.length - 1]);
        }
        v.frenzy = b.frenzy;
        v.carry = b.carry;
        // Just infected: it convulses, the coral bursting out of it.
        if (b.infected && !v.infected) {
          coralBurst(this.particles, b.pts[0], b.pts[1]);
          crabBloodSplat(this.particles, b.pts[0], b.pts[1], 20, 120);
          this.sfx?.hiss(b.pts[0], b.pts[1], true);
        }
        v.infected = b.infected;
        pushBeastSnap(v, this.frameTick);
      } else {
        // A brood out of its nest: the buzz of it (once for the lot).
        if (b.kind === BeastKind.Seeder && performance.now() - this.buzzAt > 1500) {
          this.buzzAt = performance.now();
          this.sfx?.buzz(b.pts[0], b.pts[1]);
        }
        // (Another creature in its slot now: the one before is gone (its death, if it died, follows).)
        if (v && (v.kind !== b.kind || v.seed !== b.seed)) this.beastsGone.set(b.slot, v);
        const nv: BeastView = { kind: b.kind, seed: b.seed, hp: b.hp, top: b.hp, lift: b.lift, prevLift: b.lift, attached: b.attached, frozen: b.frozen, coiled: b.coiled, gem: b.gem, frenzy: b.frenzy, carry: b.carry, infected: b.infected, prev: b.pts, cur: b.pts, snaps: [] };
        pushBeastSnap(nv, this.frameTick);
        this.beastViews.set(b.slot, nv);
      }
    }
    for (const [k, v] of this.beastViews) {
      if (seen.has(k)) continue;
      this.beastViews.delete(k);
      this.beastsGone.set(k, v);
    }
  }

  /**
   * A creature as it's drawn: its body points and how far it's reared, at
   * the render time remote clones, vehicles and craft are drawn at
   * (renderTick: a few ticks back, between two snapshots), so it stands
   * where they do relative to it (coiled round one, biting another). The
   * points are scratch: copy them to keep them.
   */
  beastPose(v: BeastView): { pts: number[]; lift: number; vx: number; vy: number } {
    const s = v.snaps;
    const rt = this.renderTick();
    let a = s[0];
    let c = s[0];
    for (let i = s.length - 1; i >= 0; i--) {
      if (s[i].tick <= rt) {
        a = s[i];
        c = s[Math.min(i + 1, s.length - 1)];
        break;
      }
    }
    const t = a === c ? 0 : Math.max(0, Math.min(1, (rt - a.tick) / (c.tick - a.tick)));
    const out = this.posePts;
    out.length = 0;
    // (A jump too far to be motion, a fall through the floor say: no sliding across it.)
    const jump = Math.abs(c.pts[0] - a.pts[0]) > 64 || Math.abs(c.pts[1] - a.pts[1]) > 64;
    for (let i = 0; i < c.pts.length; i++) out.push(jump ? c.pts[i] : a.pts[i] + (c.pts[i] - a.pts[i]) * t);
    const span = c.tick - a.tick || 1;
    return { pts: out, lift: a.lift + (c.lift - a.lift) * t, vx: ((c.pts[0] - a.pts[0]) / span) * TICK_RATE, vy: ((c.pts[1] - a.pts[1]) / span) * TICK_RATE };
  }

  beastDie(slot: number, kind: number, _seed: number): void {
    // The one that just left that slot (the death comes after the frame's list, which it's already gone
    // from). Never what's in the slot now: the server reuses a slot at once, and that's a newborn (a
    // lair's restock, a brood): taking it for the dead would gib it, and drop it while the server, thinking
    // we have it, sends only that it's unchanged (a millipede lying in wait would stay invisible).
    const v = this.beastsGone.get(slot);
    if (!v || v.kind !== kind) return;
    this.beastsGone.delete(slot);
    // Where it's drawn, not where it last was.
    v.cur = [...this.beastPose(v).pts];
    if (v.kind === BeastKind.Nest) coralBurst(this.particles, v.cur[0], v.cur[1]);
    else if (v.kind === BeastKind.Web) bloodSplat(this.particles, v.cur[0], v.cur[1], 6, 70);
    else if (v.kind === BeastKind.Seeder) bloodSplat(this.particles, v.cur[0], v.cur[1], 3, 60);
    else if (v.kind === BeastKind.Asp) aspGibs(this.particles, v.cur, v.seed);
    else if (v.kind === BeastKind.Spider) spiderGibs(this.particles, v.cur);
    // (A coral crab: its gem chips and coral crust in shards, its legs everywhere.)
    else if (v.kind === BeastKind.CoralCrab) {
      aspGibs(this.particles, v.cur, v.seed);
      spiderGibs(this.particles, v.cur);
    }
    else beastGibs(this.particles, v.cur);
    if (v.kind !== BeastKind.Nest && v.kind !== BeastKind.Web) crabBloodGibs(this.particles, v.cur);
    // Every segment flung off whole (a nest in shards; a seeder's wings torn off it).
    const n = v.kind === BeastKind.Nest ? 14 : v.kind === BeastKind.Web ? 8 + 2 * Math.min(24, Math.round(v.lift * 255)) : v.kind === BeastKind.Seeder ? 3 : v.kind === BeastKind.Spider ? 10 : v.kind === BeastKind.CoralCrab ? 0 : v.cur.length >> 1;
    for (let i = 0; i < n; i++) {
      const at = Math.min(i, (v.cur.length >> 1) - 1);
      const x = v.cur[at * 2] + (v.kind === BeastKind.Nest || v.kind === BeastKind.Web ? (Math.random() - 0.5) * 10 : 0);
      const y = v.cur[at * 2 + 1] + (v.kind === BeastKind.Nest || v.kind === BeastKind.Web ? (Math.random() - 0.5) * 10 : 0);
      const j = Math.min(n - 1, i + 1);
      const a = at < i || n < 2 ? Math.random() * Math.PI * 2 : Math.atan2(v.cur[Math.max(0, i - 1) * 2 + 1] - v.cur[j * 2 + 1], v.cur[Math.max(0, i - 1) * 2] - v.cur[j * 2]);
      const out = Math.random() * Math.PI * 2;
      const sp = 50 + Math.random() * 130;
      this.segmentGibs.push({ x, y, vx: Math.cos(out) * sp, vy: Math.sin(out) * sp - 90, a, va: (Math.random() - 0.5) * 18, kind: v.kind, seed: v.seed, i, life: 30 * 6 + Math.floor(Math.random() * 60) });
    }
    if (this.segmentGibs.length > 120) this.segmentGibs.splice(0, this.segmentGibs.length - 120);
    this.sfx?.gib(v.cur[0], v.cur[1], v.kind === BeastKind.Seeder ? 0.4 : 1.5, false);
    if (this.beastViews.get(slot) === v) this.beastViews.delete(slot);
  }
  /** Venom in clones' blood (R_VENOM): id -> the harm it still has to do. */
  readonly venomIn = new Map<number, number>();
  venom(list: { id: number; amount: number }[]): void {
    this.venomIn.clear();
    for (const v of list) this.venomIn.set(v.id, v.amount);
  }
  /** Coral seeded in clones' limbs (R_CORAL): id -> how far grown in each of CORAL_LIMBS (0..1). */
  readonly coralIn = new Map<number, number[]>();
  coral(list: { id: number; levels: number[] }[]): void {
    this.coralIn.clear();
    for (const c of list) this.coralIn.set(c.id, c.levels);
  }
  /** Ours, for the HUD (empty: none). */
  get myCoral(): readonly number[] {
    return (this.alive && this.coralIn.get(this.myId)) || [];
  }
  /** Ours, for the HUD (0: none). */
  get myVenom(): number {
    return this.alive ? (this.venomIn.get(this.myId) ?? 0) : 0;
  }

  /** Down in the valley's caves, where it's calm (the sound changes there). */
  inCalm(): boolean {
    const c = this.valley?.calm;
    return !!c && this.alive && this.body.x > c.x0 && this.body.x < c.x1 && this.body.y > c.y0;
  }

  /** A geyser blows: the same fountain of rock and shrapnel the server threw, a flash, a jolt. */
  geyserBlow(i: number, seed: number): void {
    const g = this.geyserList[i];
    if (!g) return;
    geyserBurst(this.particles, g.x, g.y, NO_OWNER, new Rng(seed));
    this.flashes.push({ x: g.x, y: g.y - 8, r: 34, at: performance.now() });
    this.geyserBlownAt.set(i, performance.now());
    const d = Math.hypot(this.body.x - g.x, this.body.y - g.y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 420) * 12);
    this.sfx?.explode(g.x, g.y, 30);
  }

  /** The dropship we're flying, if we are. */
  pilotedShip(): ShipView | null {
    if (this.pilot < 0) return null;
    return this.shipViews().find((v) => v.slot === this.pilot) ?? null;
  }

  actors(list: RemoteActor[], held: readonly number[] = []): void {
    // (Held: still there, nothing new this tick; their snapshots carry on as they are.)
    const seen = new Set<number>(held);
    for (const a of list) {
      seen.add(a.id);
      let s = this.snaps.get(a.id);
      // (A short record: its health, weapon, body and stance are as last sent. One we never had a full record of, we can't place yet.)
      const last = s?.[s.length - 1];
      if (a.short && !last) continue;
      if (!s) this.snaps.set(a.id, (s = []));
      if (a.short && last) {
        a.hp = last.hp;
        a.weapon = last.weapon;
        a.parts = last.parts;
        a.stance = last.stance;
        a.faction = last.faction;
      }
      s.push({ tick: this.frameTick, x: a.x, y: a.y, vx: a.vx, vy: a.vy, aim: a.aim, flags: a.flags, hp: a.hp, weapon: a.weapon, parts: a.parts, stance: a.stance, faction: a.faction });
      if (s.length > 12) s.shift();
      if (!(a.flags & F_ALIVE)) this.wounds.clear(a.id);
      if (a.flags & F_FIRING && a.weapon === WeaponId.Digger && a.id !== this.myId) {
        // (At the digger's nozzle, as drawn: never the clone's middle.)
        const m = this.drawnMuzzle(a.id, WEAPONS[WeaponId.Digger].muzzle, a.aim);
        if (m) digDust(this.particles, m.x, m.y, 0xa08060, 1);
      }
    }
    for (const id of this.snaps.keys()) if (!seen.has(id)) this.snaps.delete(id);
  }

  /** Enemies our side's dropships have eyes on: where, and when last reported. */
  readonly spottedFoes = new Map<number, { x: number; y: number; at: number }>();
  /** When our dropships last reported contact (for the radio callout). */
  private lastContact = -Infinity;

  spotted(list: { id: number; x: number; y: number }[]): void {
    const now = performance.now();
    let fresh = 0;
    for (const e of list) {
      if (!this.spottedFoes.has(e.id)) fresh++;
      this.spottedFoes.set(e.id, { x: e.x, y: e.y, at: now });
    }
    // A fresh contact after a quiet spell: the dropship calls it in.
    if (fresh > 0 && now - this.lastContact > 8000) {
      const me = this.ear();
      const e = list[0];
      const dir = e.x < me.x ? 'west' : 'east';
      const dist = Math.round(Math.abs(e.x - me.x) / 10) * 10;
      this.feed.push({ text: `▲ dropship: ${list.length === 1 ? 'contact' : `${list.length} contacts`}, ${dist}m ${dir}`, color: '#ff9a5a', at: now });
      if (this.feed.length > 6) this.feed.shift();
    }
    this.lastContact = now;
  }

  /** Sightings still fresh (they lapse a second after the last report). */
  spottedNow(): { id: number; x: number; y: number }[] {
    const now = performance.now();
    const out = [];
    for (const [id, e] of this.spottedFoes) {
      if (now - e.at > 1000) this.spottedFoes.delete(id);
      else out.push({ id, x: e.x, y: e.y });
    }
    return out;
  }

  blips(list: { id: number; x: number; y: number }[]): void {
    this.radar = list;
  }

  carved(x: number, y: number, r: number, seed: number, debris: number, removed: number[], detached: number[]): void {
    // Same op, same chunk state, same seed as the server: identical shower and collapse.
    releaseCarve(this.particles, removed, detached, x, y, debris, new Rng(seed));
    // Blasted cells take their blood stains with them.
    for (let i = 0; i < removed.length; i += 3) this.stain[removed[i + 1] * WORLD_W + removed[i]] = 0;
    // And any remains lying in or on what was blasted are knocked loose: a puff of them.
    if (this.remains.size && removed.length) this.knockRemains(x, y, r + 3);
    if (r <= 6 && removed.length) {
      // Digger / bullet chips: a puff of whatever was dug.
      digDust(this.particles, x, y, matRgb(removed[2]), Math.min(6, removed.length / 3));
    }
  }

  /** Remains within `r` of (x, y) knocked loose (a blast, a dig): gone from the ground, a puff of their colour. */
  private knockRemains(x: number, y: number, r: number): void {
    const r2 = r * r;
    let puffs = 0;
    for (let cy = Math.max(0, Math.floor(y - r)); cy <= Math.min(WORLD_H - 1, Math.ceil(y + r)); cy++) {
      for (let cx = Math.max(0, Math.floor(x - r)); cx <= Math.min(WORLD_W - 1, Math.ceil(x + r)); cx++) {
        if ((cx - x) ** 2 + (cy - y) ** 2 > r2) continue;
        const c = cy * WORLD_W + cx;
        const col = this.remains.get(c);
        if (col === undefined) continue;
        this.remains.delete(c);
        this.terrain.markRenderDirty(cx, cy);
        if (puffs++ < 24 && (cx + cy) % 3 === 0) {
          const rgb = ((col & 0xff) << 16) | (col & 0xff00) | ((col >> 16) & 0xff);
          this.particles.spawn(PK.Dust, cx + 0.5, cy + 0.5, (cx - x) * 8 + (Math.random() - 0.5) * 40, (cy - y) * 8 - 40 * Math.random(), 30 + Math.random() * 30, 0, rgb);
        }
      }
    }
  }

  built(piece: number, builder: number, gx: number, gy: number, placed: number[]): void {
    materialize(this.particles, placed);
    const p = pieceOf(piece);
    if (!p) return;
    // Beam from whoever built it (if we can see them).
    let bx = NaN;
    let by = NaN;
    if (builder === this.myId && this.alive) {
      bx = this.body.x + SHOULDER_X;
      by = this.body.y + SHOULDER_Y;
    } else {
      const s = this.snaps.get(builder);
      const last = s?.[s.length - 1];
      if (last) {
        bx = last.x + SHOULDER_X;
        by = last.y + SHOULDER_Y;
      }
    }
    if (!Number.isNaN(bx)) this.beams.push({ x0: bx, y0: by, x1: gx + p.w / 2, y1: gy + p.h / 2, at: performance.now() });
    if (this.beams.length > 16) this.beams.shift();
  }

  private readonly blockers: BuildBlocker[] = [];

  /**
   * Would the server accept this piece here? The same canBuild the server
   * runs, against our copy of the terrain and the bodies we can see, so the
   * ghost preview is honest.
   */
  canBuildHere(piece: number, gx: number, gy: number): number {
    const bl = this.blockers;
    bl.length = 0;
    if (this.alive) bl.push({ x: this.body.x, y: this.body.y, w: ACTOR_W, h: ACTOR_H });
    for (const [, s] of this.snaps) {
      const last = s[s.length - 1];
      if (last && last.flags & F_ALIVE) bl.push({ x: last.x, y: last.y, w: ACTOR_W, h: ACTOR_H });
    }
    for (const [, cs] of this.craftSnaps) {
      const c = cs[cs.length - 1];
      if (!c) continue;
      const e = craftHalfExtents(c.a, this.ext);
      bl.push({ x: c.x - e.x, y: c.y - e.y, w: 2 * e.x, h: 2 * e.y });
    }
    return canBuild(this.terrain, piece, gx, gy, this.body.x + SHOULDER_X, this.body.y + SHOULDER_Y, this.gold, bl);
  }

  /** Weapon in hand (WeaponId), NO_WEAPON with empty hands. */
  get weapon(): number {
    return this.inv[this.slot]?.weapon ?? NO_WEAPON;
  }

  /** Rounds in the magazine of the weapon in hand. */
  get ammo(): number {
    return this.inv[this.slot]?.ammo ?? 0;
  }

  /** Rotate through what we carry (1/2, Q/E, wheel). */
  cycle(d: number): void {
    const n = this.inv.length;
    if (n > 0 && d !== 0) this.slot = (((this.slot + d) % n) + n) % n;
  }

  /** Pick up / drop: held in the inventory byte for a few ticks; the server acts on the edge. */
  pickUp(): void {
    this.pickupHold = 3;
  }
  drop(): void {
    this.dropHold = 3;
  }

  /** This tick's inventory byte for the input command. */
  invByte(): number {
    const b = invByte(this.slot, this.invVersion, this.pickupHold > 0, this.dropHold > 0);
    if (this.pickupHold > 0) this.pickupHold--;
    if (this.dropHold > 0) this.dropHold--;
    return b;
  }

  /** The ground item we'd pick up right now, if any. */
  nearestItem(): GroundItem | null {
    if (!this.alive) return null;
    const cx = this.body.x + ACTOR_W / 2;
    const cy = this.body.y + ACTOR_H / 2;
    let best: GroundItem | null = null;
    let bestD = PICKUP_R * PICKUP_R;
    for (const [, it] of this.groundItems) {
      const d = (it.x - cx) ** 2 + (it.y - cy) ** 2;
      if (d <= bestD) {
        bestD = d;
        best = it;
      }
    }
    return best;
  }

  items(list: GroundItem[]): void {
    for (const it of list) this.groundItems.set(it.id, it);
  }

  /** Holding the materializer? */
  get building(): boolean {
    return this.alive && !this.drive && this.pilot < 0 && this.rc < 0 && WEAPONS[this.weapon]?.proj === PROJ_BUILD;
  }

  itemsGone(ids: number[]): void {
    for (const id of ids) this.groundItems.delete(id);
  }

  round(s: RoundState): void {
    this.roundState = s;
  }

  /** Every slot's team this wave (Team.None outside Last Team Standing). */
  teamOf: Uint8Array = new Uint8Array(64).fill(Team.None);

  teams(teams: Uint8Array): void {
    this.teamOf = teams;
    // Clones wear their team's colour; without a team, their own.
    for (const p of this.players.values()) {
      const c = TEAM_COLORS[teams[p.id]] ?? playerColor(p.id);
      p.color = c.css;
      p.rgb = c.rgb;
    }
  }

  /** Bunker back walls (structures.ts Backdrop per cell): cosmetic, made with the map. */
  readonly backdrop = new Uint8Array(WORLD_W * WORLD_H);

  /** Is this clone a machine (a Synth Legion body)? Its debris is scrap, not meat. */
  synthetic(id: number): boolean {
    if (this.isDroid(id)) return true;
    const f = id === this.myId ? this.body.faction : this.snaps.get(id)?.at(-1)?.faction;
    return f !== undefined && !!FACTIONS[f]?.synthetic;
  }

  /** Is this player a spider droid (body.ts ClassId.Droid)? */
  isDroid(id: number): boolean {
    if (id === this.myId) return this.body.cls === ClassId.Droid;
    const last = this.snaps.get(id)?.at(-1);
    return !!last && classOfFlags(last.flags) === ClassId.Droid;
  }

  /** Is this player a king right now (Regicide)? */
  isKing(id: number): boolean {
    const rs = this.roundState;
    return !!rs && (rs.mode === GameMode.Regicide || rs.mode === GameMode.Siege) && (rs.phase === Phase.Live || rs.phase === Phase.Victory) && (rs.kings[0] === id || rs.kings[1] === id) && id !== 255;
  }

  /** Our team this wave, Team.None if we have none. */
  get myTeam(): number {
    return this.myId >= 0 ? this.teamOf[this.myId] : Team.None;
  }

  /**
   * A new wave: the decoder has just regenerated the terrain from the seed.
   * Clear everything left from the last one, rebuild what derives from the
   * terrain, and flag any chunk that didn't come out identical to the server's.
   */
  wave(seed: number, hashes: Uint32Array, kind: number): void {
    generateWorld(this.terrain, seed, kind, this.backdrop);
    this.biome = lastBiome;
    this.mapKind = kind;
    this.caves = lastCaves !== null;
    this.deathCause = '';
    this.deathBy = null;
    this.fragged = false;
    this.relics = placeRelics(this.terrain, seed, this.backdrop, this.caves);
    this.doors = lastComplexes.flatMap((c) => c.doors ?? []);
    this.decor = placeDecor(this.terrain, lastComplexes, seed);
    this.flora = lastFlora;
    this.valley = lastValley;
    this.floraDead = new Uint8Array(lastFlora.length);
    this.floraSwell = new Set();
    this.geyserBlownAt.clear();
    this.siege = lastSiege;
    this.dungeon = lastDungeon;
    this.trapSpent = new Uint8Array(32);
    this.particles.n = 0;
    this.projectiles.n = 0;
    this.stain.fill(0);
    this.remains.clear();
    this.buried.length = 0;
    this.buriedSegments.length = 0;
    this.groundItems.clear();
    this.craftSnaps.clear();
    this.ride = null;
    this.rideSlot = -1;
    this.beams.length = 0;
    this.slugFrom.clear();
    this.projFrom.clear();
    this.flashes.length = 0;
    this.skyline.fill(WORLD_H);
    for (let ci = 0; ci < CHUNK_COUNT; ci++) {
      this.chunkLoaded(ci);
      if (this.terrain.chunkHash(ci) !== hashes[ci]) this.resyncWanted.push(ci);
    }
  }

  /** Feed the looping sounds: thrusters and jets near the ear, summed and panned toward where they are. */
  private soundBeds(sfx: Sfx): void {
    const ear = this.ear();
    sfx.listen(ear.x, ear.y);
    sfx.charge(this.laserCharge / LASER_MAX);
    sfx.calm(this.inCalm() ? 1 : 0);
    const eng = { l: 0, p: 0 };
    const jet = { l: 0, p: 0 };
    const add = (acc: { l: number; p: number }, x: number, y: number, amount: number) => {
      const d = Math.hypot(x - ear.x, y - ear.y);
      if (d > 1400 || amount <= 0) return;
      const w = amount * (1 - d / 1400) ** 2;
      acc.l += w;
      acc.p += w * Math.max(-1, Math.min(1, (x - ear.x) / 600));
    };
    for (const [, ss] of this.shipSnaps) {
      const s = ss[ss.length - 1];
      if (!s) continue;
      for (let e = 0; e < 4; e++) if (s.parts & (1 << (1 + e))) add(eng, s.x, s.y, s.thrust[e] * 0.45);
    }
    for (const [slot, cs] of this.craftSnaps) {
      const c = slot === this.rideSlot && this.ride ? this.ride : cs[cs.length - 1];
      if (c) add(eng, c.x, c.y, c.thrust);
    }
    const pr = this.projectiles;
    for (let i = 0; i < pr.n; i++) {
      const k = pr.kind[i];
      if (k === ProjKind.Engine && PROJ[k].life - pr.life[i] < (PROJ[k].burn ?? 0)) add(eng, pr.x[i], pr.y[i], 0.8);
      else if (k === ProjKind.Rocket) add(jet, pr.x[i], pr.y[i], 0.4);
    }
    if (this.alive && this.drive?.jetting) add(jet, this.drive.x, this.drive.y, 0.8);
    else if (this.alive && this.body.jetting) add(jet, this.body.x, this.body.y, 0.6);
    for (const [slot, ts] of this.tankSnaps) {
      const t = ts[ts.length - 1];
      if (t && t.jetting && slot !== this.driveSlot) add(jet, t.x, t.y, 0.8);
    }
    for (const [id, s] of this.snaps) {
      const last = s[s.length - 1];
      if (id !== this.myId && last && last.flags & F_ALIVE && last.flags & F_JET) add(jet, last.x, last.y, 0.45);
    }
    sfx.engines(eng.l, eng.l > 0 ? eng.p / eng.l : 0);
    sfx.jets(jet.l, jet.l > 0 ? jet.p / jet.l : 0);
  }

  chunkLoaded(ci: number): void {
    this.loaded[ci] = 1;
    const ox = (ci % CHUNKS_X) << CHUNK_SHIFT;
    const oy = Math.floor(ci / CHUNKS_X) << CHUNK_SHIFT;
    for (let x = ox; x < ox + CHUNK; x++) {
      if (this.skyline[x] <= oy) continue;
      const y = this.groundFrom(x, oy);
      if (y < oy + CHUNK + 64 && y < this.skyline[x]) this.skyline[x] = y;
    }
  }

  /**
   * The ground's top in column x, from `from` down: the first solid cell,
   * except that a monument's cement standing over open air (a caltrop's arm,
   * a slab's overhang) doesn't count, so the air under it shows the sky, not
   * a cave's dark.
   */
  private groundFrom(x: number, from: number): number {
    const t = this.terrain;
    // (A caltrop's cement, or a ruined building's walls and steel standing out of the ground.)
    const built = (m: number) => m === Mat.Cement || m === Mat.OldConcrete || m === Mat.Rust;
    let y = t.surfaceY(x, from);
    for (let k = 0; k < 4 && y < WORLD_H - 12 && built(t.get(x, y)); k++) {
      // A monument is pure cement down to the air under it; the crust's
      // cement pavement has char and glass beneath, so it stays ground.
      let yy = y;
      while (yy < WORLD_H - 12 && yy - y < 360 && built(t.get(x, yy))) yy++;
      if (yy - y >= 360 || yy >= WORLD_H - 12 || t.isSolid(x, yy)) break;
      y = t.surfaceY(x, yy);
    }
    return y;
  }

  projSpawn(id: number, kind: number, owner: number, x: number, y: number, vx: number, vy: number): void {
    if (this.projectiles.indexOf(id) >= 0) return;
    this.projectiles.spawn(id, kind, owner, x, y, vx, vy);
    const sp = Math.hypot(vx, vy) || 1;
    // The flash and smoke come out of the barrel as this client draws it (a
    // clone's gun: its drawn shoulder plus the weapon's muzzle length along
    // the shot), never the clone's middle, nor where the server had it a
    // moment ago (other clones are drawn a little in the past). Vehicle guns
    // fire from their own muzzles, where the server spawned the round.
    const wd = weaponOfProj(kind);
    const at = wd && owner < 64 && !this.tankPilots.has(owner) ? this.drawnMuzzle(owner, wd.muzzle, Math.atan2(vy, vx), x, y) : null;
    if (at) {
      this.projFrom.set(id, { x: at.x, y: at.y, dx: at.x - x, dy: at.y - y, at: performance.now() });
      if (this.projFrom.size > 512) this.projFrom.delete(this.projFrom.keys().next().value!);
    }
    const fx = (at ? at.x : x) + (vx / sp) * 2;
    const fy = (at ? at.y : y) + (vy / sp) * 2;
    if (kind === ProjKind.Slug) {
      this.slugFrom.set(id, { x: at ? at.x : x, y: at ? at.y : y });
      if (this.slugFrom.size > 64) this.slugFrom.delete(this.slugFrom.keys().next().value!);
      heavyMuzzle(this.particles, fx, fy, vx / sp, vy / sp);
    } else if (kind === ProjKind.Bolt) {
      // (A blaster just flashes.)
    } else if (kind === ProjKind.Venom) {
      venomSplash(this.particles, x, y, vx * 0.2, vy * 0.2, 6); // (spat: a spray out of the mouth)
    } else muzzle(this.particles, fx, fy, vx / sp, vy / sp, kind === ProjKind.Rocket || kind === ProjKind.Shell || kind === ProjKind.Missile || kind === ProjKind.SpiderMissile || kind === ProjKind.AutoShell);
    this.sfx?.shot(kind, x, y, owner);
    // Recoil: the shooter's gun kicks back (drawn), and our own shots jolt the view.
    const w = weaponOfProj(kind);
    if (w && owner < 64) {
      this.kicks.set(owner, { at: performance.now(), k: Math.min(1, 0.25 + (w.kick ?? 0) / 130) });
      if (owner === this.myId) this.shake = Math.max(this.shake, (w.kick ?? 0) / 22);
    }
  }

  /**
   * The muzzle of clone `id`'s gun as this client draws it (world): the
   * shoulder of the clone as drawn now (ours predicted, others interpolated),
   * out along `aim` by the weapon's muzzle length. Null if it isn't drawn,
   * or (given where the server fired from) if that's nowhere near it: then
   * it wasn't the clone's own gun (its tarantula's, say, fired in its name).
   */
  private drawnMuzzle(id: number, length: number, aim: number, nearX?: number, nearY?: number): { x: number; y: number } | null {
    const left = Math.cos(aim) < 0;
    let x: number;
    let y: number;
    let stance: number;
    if (id === this.myId) {
      if (!this.alive || this.drive || this.pilot >= 0 || this.rc >= 0) return null;
      const ride = this.ridingAt();
      x = ride ? ride.x : this.body.x + this.smoothX;
      y = ride ? ride.y : this.body.y + this.smoothY;
      stance = this.body.stance;
    } else {
      const v = this.remoteViews().find((r) => r.id === id);
      if (!v) return null;
      x = v.x;
      y = v.y;
      stance = v.stance;
    }
    const sh = shoulderAt(x, y, stance, left, this.muzzlePt);
    const m = { x: sh.x + Math.cos(aim) * length, y: sh.y + Math.sin(aim) * length };
    if (nearX !== undefined && nearY !== undefined && Math.hypot(m.x - nearX, m.y - nearY) > 64) return null;
    return m;
  }
  private readonly muzzlePt = { x: 0, y: 0 };

  /** The velocity of the last projectile to end (which way its strike sprays). */
  private lastVx = 0;
  private lastVy = 0;

  projEnd(id: number, x: number, y: number, kind: number, detonate: boolean, seed: number): void {
    const i = this.projectiles.indexOf(id);
    this.lastVx = i >= 0 ? this.projectiles.vx[i] : 0;
    this.lastVy = i >= 0 ? this.projectiles.vy[i] : 1;
    if (i >= 0) this.projectiles.removeAt(i);
    this.projFrom.delete(id);
    const from = this.slugFrom.get(id);
    if (from) {
      // The slug was only on screen a frame or two: what lingers is its
      // wake, a vapour trail along the whole path, and a heavy strike.
      this.slugFrom.delete(id);
      const me = this.body;
      slugTrail(this.particles, from.x, from.y, x, y, me.x, me.y, 700);
      const len = Math.hypot(x - from.x, y - from.y) || 1;
      if (detonate) {
        slugImpact(this.particles, x, y, (x - from.x) / len, (y - from.y) / len, this.dustColorAt(x, y));
        this.sfx?.impact(x, y);
      }
      return;
    }
    if (!detonate) return;
    if (kind === ProjKind.AutoShell) {
      // Solid shot: no blast, just a heavy strike (dust, sparks, smoke) where it lands.
      const sp = Math.hypot(this.lastVx, this.lastVy) || 1;
      slugImpact(this.particles, x, y, this.lastVx / sp, this.lastVy / sp, this.dustColorAt(x, y));
      this.sfx?.impact(x, y);
      return;
    }
    if (kind === ProjKind.Bolt) {
      laserHit(this.particles, x, y, this.lastVx / (Math.hypot(this.lastVx, this.lastVy) || 1), this.lastVy / (Math.hypot(this.lastVx, this.lastVy) || 1), 0.08);
      return;
    }
    if (kind === ProjKind.Venom) {
      venomSplash(this.particles, x, y);
      return;
    }
    // A mole's plasma burning out where it digs: a lick of flame, sparks, dust off the face. No blast,
    // no boom, no shake: there are twenty a second (the generic burst made the whole view judder).
    if (kind === ProjKind.Plasma) {
      plasmaTrail(this.particles, x, y, 0, 0);
      digDust(this.particles, x, y, this.dustColorAt(x, y), 1);
      return;
    }
    // A dragon's flaming blood: it bursts into fire and gore, in pixels, and that's all (no round
    // flash, no grenade's fireball: there are dozens to a breath). A quiet boom now and then, a little shake.
    if (kind === ProjKind.FireSpit) {
      fireSpitBurst(this.particles, x, y);
      if (Math.random() < 0.25) this.sfx?.explode(x, y, PROJ[kind].splashR * 0.6);
      const d = Math.hypot(this.body.x - x, this.body.y - y);
      this.shake = Math.max(this.shake, Math.max(0, 1 - d / 300) * 2.5);
      return;
    }
    if (PROJ[kind].ballistic) {
      bulletImpact(this.particles, x, y, this.dustColorAt(x, y));
      return;
    }
    const def = PROJ[kind];
    this.flashes.push({ x, y, r: def.splashR, at: performance.now() });
    // Fireball, sparks, smoke, and a blast wave in the air field that moves
    // everything loose already in flight (grains, gibs, smoke, blood).
    explosion(this.particles, x, y, kind, def.splashR, BLAST_IMPULSE, seed);
    this.sfx?.explode(x, y, def.splashR);
    const me = this.body;
    const d = Math.hypot(me.x - x, me.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 400) * 8);
  }

  /** How we died last (the death banner's headline): "killed by Rex [Sniper]", "buried in a cave-in"... */
  deathCause = '';
  /** Who killed us last, and with what (the death card), when it was another clone's own hand; else null. */
  deathBy: { id: number; weapon: string } | null = null;
  /** Died since we last came down (and not yet back): the death card shows, not LOADING OUT. */
  fragged = false;

  /** Our career record, across sessions (kept in this browser). */
  readonly career = loadCareer();

  kill(k: KillInfo): void {
    const { killer, victim, weapon } = k;
    if (victim === this.myId || (killer === this.myId && victim !== this.myId)) {
      if (victim === this.myId) this.career.deaths++;
      else this.career.kills++;
      saveCareer(this.career);
    }
    const kn = this.players.get(killer)?.name ?? '???';
    const vn = this.players.get(victim)?.name ?? '???';
    // Kills are credited by what did the damage: a projectile kind, or one of the W_* causes.
    const how = weapon === W_STONEBURNER ? 'Stoneburner' : weapon === W_BLISTER ? 'Blister Coral' : weapon === W_GEYSER ? 'Geyser' : weapon === W_ROCKFALL ? 'Falling Rock' : weapon === W_RAM ? 'Ram' : weapon === W_CRAFT ? 'Drop Rocket' : weapon === W_TANK ? 'Tank' : weapon === W_SHIP ? 'Dropship' : weapon === W_DEBRIS ? 'Debris' : weapon === W_BURN ? 'Fire' : weapon === 255 ? 'fell' : projName(weapon);
    let text: string;
    if (weapon === 255) text = `${vn} cratered`;
    else if (weapon === W_LASER && killer !== victim) text = `${kn} [Laser] ${vn}`;
    else if (killer === 255 && weapon === W_TRAP) text = `${vn} was impaled on the spikes`;
    else if (killer === 255 && weapon === ProjKind.Dart) text = `${vn} took a poisoned dart`;
    else if (killer === 255 && weapon === W_COIL) text = `${vn} was crushed and bitten by a millipede`;
    else if (killer === 255 && weapon === W_ASP) text = `${vn} was bitten by a gem asp`;
    else if (killer === 255 && weapon === W_SPIDER) text = `${vn} was torn apart by a cave spider`;
    else if (killer === 255 && weapon === W_SEED) text = `${vn} was parasitized by a seeder`;
    else if (killer === 255 && weapon === W_BASILISK) text = `${vn} was devoured by a basilisk`;
    else if (killer === 255 && weapon === W_DRAGON) text = `${vn} was devoured by a dragon`;
    else if (killer === 255 && weapon === ProjKind.FireSpit) text = `${vn} was burned alive by a dragon`;
    else if (killer === 255 && weapon === ProjKind.Venom) text = `${vn} was spat on by a millipede`;
    else if (killer === 255 && weapon === W_VENOM) text = `${vn} died of venom`;
    else if (killer === 255 && weapon === ProjKind.Mine) text = `${vn} stepped on a booby trap`;
    else if (weapon === W_STONEBURNER && (killer === victim || killer === 255)) text = `${vn} was vaporized by the Stoneburner`;
    else if (killer === victim) text = weapon === W_DEBRIS ? `${vn} was buried` : weapon === W_BURN ? `${vn} burned` : `${vn} self-destructed`;
    else text = `${kn} [${how}] ${vn}`;
    if (victim === this.myId) {
      this.deathCause = causeOfDeath(kn, killer, victim, weapon, how);
      this.deathBy = killer !== victim && killer !== 255 && this.deathCause.startsWith('killed by ') ? { id: killer, weapon: weapon === W_LASER ? 'Laser' : how } : null;
      this.fragged = true; // (even killed on the way down, before we were ever on the ground)
    }
    const droid = this.isDroid(victim);
    if (droid) text += ' (droid scrapped)';
    else if (!has(k.parts, Part.Head)) text += ' (headshot)';
    else if (!has(k.parts, Part.Torso)) text += ' (torn apart)';
    const color = victim === this.myId ? '#ff6060' : killer === this.myId ? '#80ff80' : '#e0e0e0';
    this.feed.push({ text, color, at: performance.now(), kill: true });
    if (this.feed.length > 6) this.feed.shift();

    // Gib it. Skip the work for deaths far off-screen.
    // Measured from where we are this frame (our own record comes first; the
    // body itself is only rebased after the whole frame).
    // While spectating, from whoever we're watching.
    const me = this.ear();
    const camDx = k.x - me.x;
    const camDy = k.y - me.y;
    if (victim !== this.myId && camDx * camDx + camDy * camDy > 1400 * 1400) return;
    const explosive = weapon === ProjKind.Rocket || weapon === ProjKind.Grenade || weapon === ProjKind.Shell || weapon === ProjKind.Bomb || weapon === ProjKind.Engine || weapon === ProjKind.AutoShell || weapon === ProjKind.Landmine || weapon === ProjKind.SpiderMissile || weapon === W_TANK || weapon === W_SHIP;
    const violence = k.overkill / 40 + (explosive ? 1.5 : 0) + (weapon === 255 ? 0.5 : 0);
    if (droid) droidWreck(this.particles, k.x, k.y, k.vx, k.vy, k.parts, violence);
    else gibBurst(this.particles, k.x, k.y, k.vx, k.vy, this.players.get(victim)?.rgb ?? 0xcccccc, violence, k.parts, this.synthetic(victim));
    this.sfx?.gib(k.x, k.y, violence, this.synthetic(victim));
    // Same seed as the server, so the gold shower matches what will settle.
    spillGold(this.particles, k.x, k.y, k.vx, k.vy, k.gold, new Rng(k.seed));
    this.snaps.delete(victim);
  }

  roster(id: number, present: boolean, name: string): void {
    if (!present) {
      this.players.delete(id);
      this.snaps.delete(id);
      return;
    }
    const c = TEAM_COLORS[this.teamOf[id]] ?? playerColor(id);
    name = inert(name, 20);
    this.players.set(id, { id, name, kills: 0, deaths: 0, gold: 0, wins: 0, beasts: 0, teamKills: 0, bot: name.startsWith('BOT '), color: c.css, rgb: c.rgb });
  }

  scores(list: { id: number; kills: number; deaths: number; gold: number; wins: number; beasts: number; teamKills: number }[]): void {
    for (const s of list) {
      const p = this.players.get(s.id);
      if (p) {
        p.kills = s.kills;
        p.deaths = s.deaths;
        p.gold = s.gold;
        p.wins = s.wins;
        p.beasts = s.beasts;
        p.teamKills = s.teamKills;
      }
    }
  }

  /**
   * Wounds bleed (wounds.ts): now and then a drop off a clone's open wounds
   * (not a droid's, nor the Legion's oil), and off a hurt creature's worst
   * cuts, more the worse it is; a badly hurt dragon's leak burning blood
   * and smoke. Cosmetic, on each client, from what it already knows.
   */
  private woundDrips(): void {
    for (const [id, s] of this.snaps) {
      const last = s[s.length - 1];
      if (!last || !(last.flags & F_ALIVE) || last.hp >= ACTOR_MAX_HP || classOfFlags(last.flags) === ClassId.Droid || FACTIONS[last.faction]?.synthetic) continue;
      const shown = this.wounds.shown(id, (last.hp / ACTOR_MAX_HP) * 100);
      if (!shown.length || Math.random() > 0.02 * shown.length) continue;
      const w = shown[Math.floor(Math.random() * shown.length)];
      const face = Math.cos(dequantizeAim(last.aim)) < 0 ? -1 : 1;
      bloodSplat(this.particles, last.x + HIP_X + 0.5 + w.u * face, last.y + HIP_Y + w.v, 1, 6 + w.r * 6);
    }
    for (const [, v] of this.beastViews) {
      // A dragon breathing (its jaws held part open, not flung wide roaring): fire pouring from its mouth.
      if (v.kind === BeastKind.Dragon && v.frenzy && v.lift > 0.3 && v.lift < 0.8 && v.cur.length >= 4) dragonBreathGout(this.particles, v.cur[0], v.cur[1], Math.atan2(v.cur[1] - v.cur[3], v.cur[0] - v.cur[2]));
      const dmg = beastDamage(v.hp, v.top);
      if (dmg < 0.15 || v.gem || v.cur.length < 4 || Math.random() > dmg * 0.25) continue;
      const scars = beastScars(v.seed, v.cur.length >> 1, dmg).filter((c) => c.sev > 0.45);
      if (!scars.length) continue;
      const c = scars[Math.floor(Math.random() * scars.length)];
      const x = v.cur[c.seg * 2];
      const y = v.cur[c.seg * 2 + 1];
      if (v.kind === BeastKind.Dragon) {
        if (c.sev > 0.7) fireSpitTrail(this.particles, x, y, 0, 30);
        else bloodSplat(this.particles, x, y, 2, 20);
      } else if (v.kind === BeastKind.Spider) ichorSplat(this.particles, x, y, 1, 10);
      else if (v.kind === BeastKind.Asp) gooSplat(this.particles, x, y, v.seed, 1, 10);
      else if (v.kind === BeastKind.Basilisk || v.kind === BeastKind.Millipede) bloodSplat(this.particles, x, y, 1, 10);
      // (And its blue, from every wound.)
      if (v.kind !== BeastKind.Nest && v.kind !== BeastKind.Web) crabBloodSplat(this.particles, x, y, 1, 10);
    }
  }

  hit(victim: number, x: number, y: number, amount: number): void {
    // A wound where it struck, pinned in the body's own frame (from its hip, as it faces).
    const last = this.snaps.get(victim)?.at(-1);
    const me = victim === this.myId;
    const bx = me ? this.body.x : last?.x;
    const by = me ? this.body.y : last?.y;
    if (bx !== undefined && by !== undefined) {
      const face = (me ? this.myLeft : Math.cos(dequantizeAim(last!.aim)) < 0) ? -1 : 1;
      const u = (x - (bx + HIP_X + 0.5)) * face;
      const v = y - (by + HIP_Y);
      // (A hit well off the body (a blast's centre): on the nearest of it.)
      this.wounds.hit(victim, Math.max(-3.5, Math.min(3.5, u)), Math.max(-9, Math.min(5, v)), amount, performance.now());
    }
    // (A droid sparks where it's hit; anything else bleeds.)
    if (this.isDroid(victim)) droidHit(this.particles, x, y, amount);
    else bloodSplat(this.particles, x, y, Math.min(24, 3 + amount / 3), 60 + amount * 1.5);
    this.sfx?.hit(x, y, amount, this.synthetic(victim));
    if (victim === this.myId) this.hurtFlash = Math.min(1, this.hurtFlash + amount / 60);
  }

  chat(id: number, text: string): void {
    const p = this.players.get(id);
    this.chatLog.push({ text: `${p?.name ?? '?'}: ${cleanChat(text)}`, color: p?.color ?? '#ccc', at: performance.now() });
    if (this.chatLog.length > 8) this.chatLog.shift();
  }

  crafts(list: CraftState[]): void {
    const seen = new Set<number>();
    for (const c of list) {
      seen.add(c.slot);
      let s = this.craftSnaps.get(c.slot);
      if (!s) this.craftSnaps.set(c.slot, (s = []));
      s.push({ ...c, tick: this.frameTick });
      if (s.length > 12) s.shift();
    }
    this.craftsSeenTick = this.frameTick;
    for (const slot of this.craftSnaps.keys()) if (!seen.has(slot)) this.craftSnaps.delete(slot);
  }
  private craftsSeenTick = 0;

  selfCraft(s: SelfCraftState): void {
    this.lastSelfCraft = s;
  }

  /** A drop rocket's colours, as drawn: its rider's, else bare gunmetal. */
  private craftColor(c: CraftState | undefined): number {
    return c && c.passenger !== 255 ? (this.players.get(c.passenger)?.rgb ?? CRAFT_GUNMETAL) : CRAFT_GUNMETAL;
  }

  craftPart(slot: number, part: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    craftPartOff(this.particles, part, x, y, vx, vy, seed, this.craftColor(this.craftSnaps.get(slot)?.at(-1)));
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 300) * 4);
  }

  craftBoom(slot: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    // (As last heard of: whose it was, and whether its nose cone was still on.)
    const was = this.craftSnaps.get(slot)?.at(-1);
    this.craftSnaps.delete(slot);
    if (slot === this.rideSlot) {
      this.ride = null;
      this.rideSlot = -1;
    }
    craftDebris(this.particles, x, y, vx, vy, seed, BLAST_IMPULSE, this.craftColor(was), !was || (was.parts & (1 << CraftPart.Nose)) !== 0);
    this.flashes.push({ x, y, r: 40, at: performance.now() });
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 500) * 12);
  }

  tanks(list: TankState[], held: readonly number[] = []): void {
    const seen = new Set<number>(held);
    this.tankPilots.clear();
    // (A held vehicle keeps its pilot as last sent.)
    for (const slot of held) {
      const last = this.tankSnaps.get(slot)?.at(-1);
      if (last && last.pilot !== 255 && !last.remote) this.tankPilots.add(last.pilot);
    }
    for (const t of list) {
      seen.add(t.slot);
      if (t.pilot !== 255 && !t.remote) this.tankPilots.add(t.pilot);
      let s = this.tankSnaps.get(t.slot);
      if (!s) this.tankSnaps.set(t.slot, (s = []));
      s.push({ ...t, tick: this.frameTick });
      if (s.length > 12) s.shift();
    }
    this.tanksSeenTick = this.frameTick;
    for (const slot of this.tankSnaps.keys()) if (!seen.has(slot)) this.tankSnaps.delete(slot);
  }
  private tanksSeenTick = 0;

  selfTank(s: SelfTankState): void {
    this.lastSelfTank = s;
  }

  tankPart(_slot: number, part: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    tankPartOff(this.particles, part, x, y, vx, vy, seed);
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 300) * 5);
  }

  tankBoom(slot: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    this.tankSnaps.delete(slot);
    tankDebris(this.particles, x, y, vx, vy, seed, BLAST_IMPULSE);
    this.flashes.push({ x, y, r: 56, at: performance.now() });
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 600) * 16);
  }

  ships(list: ShipState[]): void {
    const seen = new Set<number>();
    for (const s of list) {
      seen.add(s.slot);
      let q = this.shipSnaps.get(s.slot);
      if (!q) this.shipSnaps.set(s.slot, (q = []));
      q.push({ ...s, tick: this.frameTick });
      if (q.length > 12) q.shift();
    }
    this.shipsSeenTick = this.frameTick;
    for (const slot of this.shipSnaps.keys()) if (!seen.has(slot)) this.shipSnaps.delete(slot);
  }

  shipPart(_slot: number, part: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    tankPartOff(this.particles, part === 5 ? 0 : 1, x, y, vx, vy, seed, SHIP_SCRAP);
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 300) * 5);
  }

  shipBoom(slot: number, x: number, y: number, vx: number, vy: number, seed: number): void {
    this.shipSnaps.delete(slot);
    tankDebris(this.particles, x, y, vx, vy, seed, BLAST_IMPULSE, SHIP_SCRAP);
    this.flashes.push({ x, y, r: 70, at: performance.now() });
    const d = Math.hypot(this.body.x - x, this.body.y - y);
    this.shake = Math.max(this.shake, Math.max(0, 1 - d / 700) * 18);
  }

  /** Dropships at the render time, interpolated between snapshots. */
  shipViews(): ShipView[] {
    const rt = this.renderTick();
    const out: ShipView[] = [];
    for (const [, s] of this.shipSnaps) {
      const last = s[s.length - 1];
      let a = s[0];
      let c = s[0];
      for (let i = s.length - 1; i >= 0; i--) {
        if (s[i].tick <= rt) {
          a = s[i];
          c = s[Math.min(i + 1, s.length - 1)];
          break;
        }
      }
      const t = a === c ? 0 : Math.max(0, Math.min(1, (rt - a.tick) / (c.tick - a.tick)));
      out.push({ ...c, fired: last.fired, doors: last.doors, x: a.x + (c.x - a.x) * t, y: a.y + (c.y - a.y) * t, a: a.a + (c.a - a.a) * t });
    }
    return out;
  }

  /** The radio is in hand: the call-in menu is up. */
  get calling(): boolean {
    // (Not while at a remote: then clicks are the dropship's or the watchdog's guns.)
    return this.alive && !this.drive && this.pilot < 0 && this.rc < 0 && this.weapon === WeaponId.Radio;
  }

  /** Tanks at the render time, interpolated between snapshots (ours from prediction). */
  tankViews(alpha = 1): TankView[] {
    const rt = this.renderTick();
    const out: TankView[] = [];
    for (const [slot, s] of this.tankSnaps) {
      const b = s[s.length - 1];
      if (slot === this.driveSlot && this.drive && this.alive) {
        // Ours: wherever our seat is this frame.
        const pb = this.prevBody;
        const me = this.body;
        out.push({ ...b, x: pb.x + (me.x - pb.x) * alpha + this.smoothX - SEAT_X, y: pb.y + (me.y - pb.y) * alpha + this.smoothY - SEAT_Y, parts: this.drive.parts, chute: false, jetting: this.drive.jetting, a: this.drive.a });
        continue;
      }
      let a = s[0];
      let c = s[0];
      for (let i = s.length - 1; i >= 0; i--) {
        if (s[i].tick <= rt) {
          a = s[i];
          c = s[Math.min(i + 1, s.length - 1)];
          break;
        }
      }
      const t = a === c ? 0 : Math.max(0, Math.min(1, (rt - a.tick) / (c.tick - a.tick)));
      out.push({ ...c, firedSmg: b.firedSmg, firedCannon: b.firedCannon, x: a.x + (c.x - a.x) * t, y: a.y + (c.y - a.y) * t, a: a.a + (c.a - a.a) * t });
    }
    return out;
  }

  /** The empty, landed tank we could climb into right now, if any. */
  boardableTank(): TankView | null {
    if (!this.alive || this.drive) return null;
    const b = this.body;
    for (const [, s] of this.tankSnaps) {
      const t = s[s.length - 1];
      if (t.pilot !== 255 || t.chute || isPet(t)) continue;
      const dx = Math.max(t.x - (b.x + ACTOR_W), 0, b.x - (t.x + tankW(t)));
      const dy = Math.max(t.y - (b.y + ACTOR_H), 0, b.y - (t.y + tankH(t)));
      if (dx <= 10 && dy <= 10) return t;
    }
    return null;
  }

  /**
   * The friendly vehicle we could ride on top of right now (tank surfing;
   * the server's rule: a teammate's tank, watchdog or tarantula, or our own
   * watchdog or tarantula), if any.
   */
  surfableTank(): TankView | null {
    if (!this.alive || this.drive || this.surf >= 0 || this.surfShip >= 0) return null;
    const b = this.body;
    const team = this.myTeam;
    for (const t of this.tankViews()) {
      if (t.chute) continue;
      const who = isPet(t) ? t.owner : t.pilot;
      const ok = who === this.myId ? isPet(t) : who !== 255 && team !== Team.None && this.teamOf[who] === team;
      if (!ok) continue;
      const dx = Math.max(t.x - (b.x + ACTOR_W), 0, b.x - (t.x + tankW(t)));
      const dy = Math.max(t.y - (b.y + ACTOR_H), 0, b.y - (t.y + tankH(t)));
      if (dx <= 14 && dy <= 14) return t;
    }
    return null;
  }

  /**
   * The friendly dropship we could climb onto the roof of right now (ours,
   * or with teams our side's; a free seat; one within reach, the server's
   * rule), if any.
   */
  surfableShip(): ShipView | null {
    if (!this.alive || this.drive || this.surf >= 0 || this.surfShip >= 0) return null;
    const b = this.body;
    const team = this.myTeam;
    const pt = { x: 0, y: 0 };
    for (const sh of this.shipViews()) {
      if (sh.leaving || sh.riders.length >= SHIP_RIDERS) continue;
      if (sh.owner !== this.myId && (team === Team.None || sh.team !== team)) continue;
      for (let i = 0; i < SHIP_RIDERS; i++) {
        const s = shipSeat(sh, i, pt);
        if (Math.abs(s.x - (b.x + ACTOR_W / 2)) + Math.abs(s.y - (b.y + ACTOR_H)) * 0.5 < 26) return sh;
      }
    }
    return null;
  }

  /** Drop rockets at the render time, interpolated between snapshots. */
  craftViews(): CraftView[] {
    const rt = this.renderTick();
    const out: CraftView[] = [];
    for (const [, s] of this.craftSnaps) {
      let a = s[0];
      let b = s[0];
      for (let i = s.length - 1; i >= 0; i--) {
        if (s[i].tick <= rt) {
          a = s[i];
          b = s[Math.min(i + 1, s.length - 1)];
          break;
        }
      }
      if (b.slot === this.rideSlot && this.ride) continue; // drawn from prediction
      const t = a === b ? 0 : Math.max(0, Math.min(1, (rt - a.tick) / (b.tick - a.tick)));
      out.push({ ...b, x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, a: wrapAngle(a.a + wrapAngle(b.a - a.a) * t) });
    }
    return out;
  }

  /** The drop rocket carrying this player in, if any: predicted, interpolated by `alpha`. */
  myCraft(alpha = 1): CraftView | null {
    if (this.alive) return null;
    const r = this.ride;
    if (r) {
      const p = this.ridePrev;
      return {
        slot: this.rideSlot,
        x: p.x + (r.x - p.x) * alpha + this.rideSmoothX,
        y: p.y + (r.y - p.y) * alpha + this.rideSmoothY,
        vx: r.vx,
        vy: r.vy,
        a: p.a + wrapAngle(r.a - p.a) * alpha + this.rideSmoothA,
        thrust: r.thrust,
        hp: r.hp,
        passenger: this.myId,
        phase: r.phase,
        parts: r.parts,
      };
    }
    for (const c of this.craftViews()) if (c.passenger === this.myId) return c;
    return null;
  }

  detach(id: number, part: number, x: number, y: number, vx: number, vy: number): void {
    // A limb with coral in it: the coral bursts out with it, in a spray of pink.
    const ci = CORAL_LIMBS.indexOf(part as (typeof CORAL_LIMBS)[number]);
    if (ci >= 0 && (this.coralIn.get(id)?.[ci] ?? 0) > 0) coralBurst(this.particles, x, y);
    if (this.isDroid(id)) droidPartOff(this.particles, part, x, y, vx, vy);
    else limbOff(this.particles, part, x, y, vx, vy, this.players.get(id)?.rgb ?? 0xcccccc, this.synthetic(id));
    this.sfx?.limb(x, y, this.synthetic(id));
    if (id === this.myId) this.hurtFlash = 1;
  }

  /** Colour of the terrain around a point (for dust puffs). */
  private dustColorAt(x: number, y: number): number {
    const cx = Math.floor(x);
    const cy = Math.floor(y);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const m = this.terrain.get(cx + dx, cy + dy);
        if (m !== Mat.Air) return matRgb(m);
      }
    return 0x9a8060;
  }

  /** Live particles of one kind (HUD). */
  countKind(kind: number): number {
    return this.particles.count(kind);
  }

  colorOf(id: number): string {
    return this.players.get(id)?.color ?? '#ccc';
  }
}

/**
 * What killed us, for the death banner, in the kill feed's terms: who, and
 * with what ("killed by Rex [Sniper]"), or what happened ("buried in a
 * cave-in", "crushed by falling rock", "fell to your death").
 */
export function causeOfDeath(killerName: string, killer: number, victim: number, weapon: number, how: string): string {
  const by = killer !== victim && killer !== 255 ? killerName : '';
  if (weapon === 255) return 'fell to your death';
  if (weapon === W_TRAP) return 'impaled on the spikes';
  if (killer === 255 && weapon === ProjKind.Dart) return 'took a poisoned dart';
  if (killer === 255 && weapon === W_COIL) return 'crushed and bitten by a millipede';
  if (killer === 255 && weapon === W_ASP) return 'bitten by a gem asp';
  if (killer === 255 && weapon === W_SPIDER) return 'torn apart by a cave spider';
  if (killer === 255 && weapon === W_SEED) return 'parasitized by seeder';
  if (killer === 255 && weapon === W_BASILISK) return 'devoured by a basilisk';
  if (killer === 255 && weapon === W_DRAGON) return 'devoured by a dragon';
  if (killer === 255 && weapon === ProjKind.FireSpit) return 'burned alive by a dragon';
  if (killer === 255 && weapon === ProjKind.Venom) return "spat on by a millipede";
  if (killer === 255 && weapon === W_VENOM) return 'died of venom';
  if (killer === 255 && weapon === ProjKind.Mine) return 'stepped on a booby trap';
  if (weapon === W_STONEBURNER) return by ? `vaporized by the Stoneburner [${by} called it down]` : 'vaporized by the Stoneburner';
  if (weapon === W_GEYSER) return by ? `caught in a geyser's blast [${by} set it off]` : "caught in a geyser's blast";
  if (weapon === W_BLISTER) return by ? `scalded by a bursting blister coral [${by} burst it]` : 'scalded by a bursting blister coral';
  if (weapon === W_ROCKFALL) return by ? `crushed by falling rock [${by} brought it down]` : 'crushed by falling rock';
  if (weapon === W_DEBRIS) return by ? `buried in a cave-in [${by}'s doing]` : 'buried in a cave-in';
  if (weapon === W_BURN) return by ? `burned to death [${by}]` : 'burned to death';
  if (!by) return weapon === W_CRAFT ? 'crushed by a drop rocket' : weapon === W_TANK ? 'went down with the tank' : 'killed yourself';
  return `killed by ${by} [${weapon === W_LASER ? 'Laser' : how}]`;
}
